// stream-based-calculations.js
/* eslint-disable no-unused-vars */
import {
  COMPONENTS,
  R,
  calculateMixtureProperties,
  pengRobinsonZ,
  calculateDensity,
  calculateEnthalpyDeparture,
  calculateIdealGasEnthalpyChange,
  calculateIsentropicTemperature,
  calculateCompressionPower,
  calculateGamma,
  calculateHeatExchangerDuty,
  calculateVLE
} from './fluid-properties';

/**
 * Stream class to represent material flows between process units
 */
class Stream {
    constructor(temperature, pressure, flowRate, composition) {
        this.temperature = temperature || 25; // Default temperature (°C)
        this.pressure = pressure || 1; // Default pressure (bar) 
        this.flowRate = flowRate || 0; // Default flow rate (kmol/h)
        this.composition = { ...(composition || {}) }; // Default to empty composition
        this.phase = 'vapor'; // 'vapor', 'liquid', or 'mixed'
        
        // Make sure components is properly initialized
        this.components = Object.keys(this.composition)
          .map(name => Object.values(COMPONENTS).find(comp => comp.name === name))
          .filter(comp => comp !== undefined);
        
        // Only call refreshProperties if we have components
        if (this.components.length > 0) {
          this.refreshProperties();
        } else {
          // Initialize with default properties
          this.properties = {
            MW: 0,
            Tc: 0,
            Pc: 0,
            omega: 0,
            Z: 1
          };
          this.density = 0;
          this.massFlow = 0;
        }
      }
  
  /**
   * Calculate and update stream properties
   */
  refreshProperties() {
    // Map composition to array format for calculations
    const compositionValues = Object.keys(this.composition)
      .map(name => this.composition[name])
      .filter((_, index) => this.components[index] !== undefined);
    
    // Calculate mixture properties
    this.properties = calculateMixtureProperties(this.components, compositionValues);
    
    // Calculate Z factor
    const zFactor = pengRobinsonZ(
      this.temperature + 273.15, 
      this.pressure, 
      this.properties.Tc, 
      this.properties.Pc, 
      this.properties.omega
    );
    
    this.properties.Z = zFactor.Z;
    
    // Calculate density
    this.density = calculateDensity(
      this.properties.MW,
      this.pressure,
      this.properties.Z,
      this.temperature + 273.15
    ); // kg/m³
    
    // Calculate mass flow
    this.massFlow = this.flowRate * this.properties.MW / 1000; // tonnes/h
  }
  
  /**
   * Create a copy of the stream
   * @returns {Stream} A new stream with the same properties
   */
  clone() {
    return new Stream(
      this.temperature,
      this.pressure,
      this.flowRate,
      this.composition
    );
  }
  
  /**
   * Combine with another stream
   * @param {Stream} otherStream - The stream to mix with
   * @returns {Stream} A new stream representing the mixture
   */
  mix(otherStream) {
    // Calculate new flow rate
    const newFlowRate = this.flowRate + otherStream.flowRate;
    
    // Calculate new composition (weighted average)
    const newComposition = {};
    
    // Initialize with all components from both streams
    const allComponents = new Set([
      ...Object.keys(this.composition),
      ...Object.keys(otherStream.composition)
    ]);
    
    allComponents.forEach(component => {
      // Get mole fractions (0 if not present)
      const thisComp = this.composition[component] || 0;
      const otherComp = otherStream.composition[component] || 0;
      
      // Calculate weighted average
      newComposition[component] = 
        (thisComp * this.flowRate + otherComp * otherStream.flowRate) / newFlowRate;
    });
    
    // Calculate new temperature (simplified energy balance)
    // Using weighted average based on molar flow
    const newTemperature = 
      (this.temperature * this.flowRate + otherStream.temperature * otherStream.flowRate) / newFlowRate;
    
    // Use the lower pressure of the two streams (conservative approach)
    const newPressure = Math.min(this.pressure, otherStream.pressure);
    
    // Create new mixed stream
    return new Stream(newTemperature, newPressure, newFlowRate, newComposition);
  }
  
  /**
   * Split the stream into two based on a split fraction
   * @param {number} splitFraction - Fraction of the stream to first output (0-1)
   * @returns {Array} Array of two streams [splitStream, remainderStream]
   */
  split(splitFraction) {
    if (splitFraction < 0 || splitFraction > 1) {
      throw new Error("Split fraction must be between 0 and 1");
    }
    
    // Create two new streams with the same properties but scaled flow rates
    const splitStream = this.clone();
    splitStream.flowRate *= splitFraction;
    
    const remainderStream = this.clone();
    remainderStream.flowRate *= (1 - splitFraction);
    
    return [splitStream, remainderStream];
  }
  
  /**
   * Perform vapor-liquid equilibrium calculation
   * @param {Object} options - Options for VLE calculation
   * @returns {Object} Object containing vapor and liquid streams
   */
    /**
     * Perform vapor-liquid equilibrium calculation
     * @param {Object} options - Options for VLE calculation
     * @returns {Object} Object containing vapor and liquid streams
     */
    vaporLiquidSeparation(options = {}) {
        // Get temperature and pressure
        const tempK = this.temperature + 273.15; // Convert to K
        const pressure = this.pressure; // bar
        
        // Calculate vapor pressures for each component at this temperature
        const vaporPressures = {};
        Object.keys(this.composition).forEach(component => {
        const comp = Object.values(COMPONENTS).find(c => c.name === component);
        if (comp) {
            // Calculate vapor pressure using reduced properties
            const Tr = tempK / comp.Tc; // Reduced temperature
            if (Tr < 1) { // Subcritical
            // Approximate vapor pressure using critical properties and acentric factor
            const omega = comp.omega; // Acentric factor
            const Pvp = comp.Pc * Math.exp(5.37 * (1 + omega) * (1 - 1/Tr));
            vaporPressures[component] = Pvp; // bar
            } else {
            // Supercritical - component is always vapor
            vaporPressures[component] = 1000; // Much higher than system pressure
            }
        } else {
            vaporPressures[component] = pressure; // Default if component not found
        }
        });
        
        // Calculate K-values (vapor/liquid ratio) for each component
        // K = vapor pressure / system pressure (Raoult's Law)
        const kValues = {};
        Object.keys(this.composition).forEach(component => {
        kValues[component] = vaporPressures[component] / pressure;
        });
        
        // Function to evaluate Rachford-Rice equation: sum(z_i*(K_i-1)/(1+V*(K_i-1))) = 0
        const rachfordRice = (V) => {
        let sum = 0;
        Object.entries(this.composition).forEach(([component, zi]) => {
            const Ki = kValues[component];
            if (zi > 0 && Ki > 0) {
            sum += zi * (Ki - 1) / (1 + V * (Ki - 1));
            }
        });
        return sum;
        };
        
        // Determine if this mixture has both vapor and liquid phases at these conditions
        const minK = Math.min(...Object.values(kValues).filter(k => k > 0));
        const maxK = Math.max(...Object.values(kValues));
        
        let vaporFraction;
        
        if (minK >= 1) {
        // All components have K ≥ 1, so mixture is 100% vapor
        vaporFraction = 1.0;
        } else if (maxK <= 1) {
        // All components have K ≤ 1, so mixture is 100% liquid
        vaporFraction = 0.0;
        } else {
        // Two-phase region - solve for vapor fraction using bisection method
        let Vmin = 0;
        let Vmax = 1;
        vaporFraction = 0.5; // Initial guess
        const tolerance = 1e-6;
        let iterations = 0;
        
        while (Math.abs(rachfordRice(vaporFraction)) > tolerance && iterations < 100) {
            if (rachfordRice(vaporFraction) * rachfordRice(Vmin) < 0) {
            Vmax = vaporFraction;
            } else {
            Vmin = vaporFraction;
            }
            vaporFraction = (Vmin + Vmax) / 2;
            iterations++;
        }
        }
        
        // Initialize vapor and liquid compositions
        const vaporComposition = {};
        const liquidComposition = {};
        
        // Calculate vapor and liquid compositions
        if (vaporFraction === 1.0) {
        // 100% vapor - vapor composition equals feed composition
        Object.entries(this.composition).forEach(([component, zi]) => {
            vaporComposition[component] = zi;
            liquidComposition[component] = 0;
        });
        } else if (vaporFraction === 0.0) {
        // 100% liquid - liquid composition equals feed composition
        Object.entries(this.composition).forEach(([component, zi]) => {
            vaporComposition[component] = 0;
            liquidComposition[component] = zi;
        });
        } else {
        // Two-phase mixture - calculate compositions using K-values
        Object.entries(this.composition).forEach(([component, zi]) => {
            const Ki = kValues[component];
            
            // Liquid composition: x_i = z_i / (1 + V*(K_i-1))
            const xi = zi / (1 + vaporFraction * (Ki - 1));
            
            // Vapor composition: y_i = K_i * x_i
            const yi = Ki * xi;
            
            liquidComposition[component] = xi;
            vaporComposition[component] = yi;
        });
        }
        
        // Normalize compositions to ensure they sum to 1
        const vaporSum = Object.values(vaporComposition).reduce((sum, val) => sum + val, 0);
        const liquidSum = Object.values(liquidComposition).reduce((sum, val) => sum + val, 0);
        
        if (vaporSum > 0) {
        Object.keys(vaporComposition).forEach(component => {
            vaporComposition[component] /= vaporSum;
        });
        }
        
        if (liquidSum > 0) {
        Object.keys(liquidComposition).forEach(component => {
            liquidComposition[component] /= liquidSum;
        });
        }
        
        // Create vapor and liquid streams
        const vaporStream = new Stream(
        this.temperature,
        this.pressure,
        this.flowRate * vaporFraction,
        vaporComposition
        );
        
        const liquidStream = new Stream(
        this.temperature,
        this.pressure,
        this.flowRate * (1 - vaporFraction),
        liquidComposition
        );
        
        vaporStream.phase = 'vapor';
        liquidStream.phase = 'liquid';
        
        return {
        vaporStream,
        liquidStream,
        vaporFraction
        };
    }
}

/**
 * Plant Model class to handle the entire process flow and calculations
 */
class PlantModel {
  constructor(feedComposition, feedConditions, activeUnits, unitConfigurations) {
    this.feedComposition = feedComposition;
    this.feedConditions = feedConditions;
    this.activeUnits = activeUnits;
    this.unitConfigurations = unitConfigurations;
    
    // Initialize streams between units
    this.streams = {};
    
    // Initialize energy consumption for each unit
    this.energyConsumption = {};
    
    // Initialize output compositions at each stage
    this.stageCompositions = {};
    
    // Initialize recycle streams
    this.recycleStreams = {};
    
    // Create feed stream
    this.streams.feed = new Stream(
      feedConditions.temperature,
      feedConditions.pressure,
      feedConditions.flowRate,
      feedComposition
    );
  }
  
  /**
   * Calculate the entire plant
   * @param {number} maxIterations - Maximum number of recycle iterations
   * @returns {Object} Calculation results
   */
  calculate(maxIterations = 10) {
    // Initialize total energy consumption
    let totalEnergyConsumption = 0;
    
    // Initialize streams
    this.initializeStreams();
    
    // Perform iterations to handle recycle streams
    for (let iteration = 0; iteration < maxIterations; iteration++) {
      console.log(`Iteration ${iteration + 1}`);
      // Reset temporary streams for this iteration
      this.tempStreams = {};
      // Process each unit in sequence
      if (this.activeUnits.fieldCompression) {
        this.processFieldCompression();
      }
      
      if (this.activeUnits.inletSeparation) {
        this.processInletSeparation();
      }
      
      if (this.activeUnits.inletCompression) {
        // If we have flash gas from sweetening, add it to the inlet compression feed
      if (this.tempStreams.sweeteningFlashGas && this.tempStreams.sweeteningFlashGas.flowRate > 0) {
        if (this.streams.inletCompressionIn) {
          this.streams.inletCompressionIn = this.streams.inletCompressionIn.mix(
            this.tempStreams.sweeteningFlashGas
          );
        } else {
          this.streams.inletCompressionIn = this.tempStreams.sweeteningFlashGas;
        }
        }
        this.processInletCompression();
      }
      
      if (this.activeUnits.stabilisation) {
        this.processStabilisation();
      }
      
      if (this.activeUnits.wasteWaterDisposal) {
        this.processWasteWaterDisposal();
      }
      
      if (this.activeUnits.sweetening) {
        this.processSweetening();
        this.processSulfurRecovery();
        // Store flash gas for routing to inlet compression
        if (this.streams.flashGasOut && this.streams.flashGasOut.flowRate > 0) {
            this.tempStreams.sweeteningFlashGas = this.streams.flashGasOut.clone();
        }
      }

      
      if (this.activeUnits.dehydration) {
        this.processDehydration();
      }
      
      if (this.activeUnits.dewpoint) {
        this.processDewpoint();
      }
      
      if (this.activeUnits.fractionation) {
        this.processFractionation();
      }
      
      if (this.activeUnits.salesCompression) {
        this.processSalesCompression();
      }
      
      // Check for convergence of recycle streams
      if (this.checkConvergence()) {
        console.log(`Converged after ${iteration + 1} iterations`);
        break;
      }
      
      // Update recycle streams for next iteration
      this.updateRecycleStreams();
    }
    
    // Calculate utilities
    if (this.activeUnits.utilities !== 'none') {
      this.processUtilities();
    }
    let totalCO2Emissions = 0;
    // Calculate total energy consumption
    for (const unit in this.energyConsumption) {
      // Skip utilities for now, will handle separately
        if (unit === 'utilities') continue;
        
        const unitEnergy = this.energyConsumption[unit];
        
        // Add power consumption
        if (typeof unitEnergy.power === 'number') {
        // For SRU, may have negative value (heat recovery)
        totalEnergyConsumption += unitEnergy.power;
        }
        
        // Add CO2 emissions
        if (typeof unitEnergy.co2Emissions === 'number') {
        totalCO2Emissions += unitEnergy.co2Emissions;
        }
    }


    // Now handle utilities properly
    if (this.energyConsumption.utilities) {
        const utilities = this.energyConsumption.utilities;
        
        // Add only the inefficiency losses, not the total fuel
        // This avoids double-counting the energy already accounted for in process units
        totalEnergyConsumption += utilities.power;
        
        // Add CO2 emissions from utilities
        totalCO2Emissions += utilities.co2Emissions;
    }
    

    
    // Calculate specific energy consumption
    const specificEnergyConsumption = totalEnergyConsumption / this.feedConditions.flowRate;
    
    return {
      totalEnergyConsumption, // MW
      unitEnergyConsumption: this.energyConsumption,
      totalCO2Emissions, // tonnes/day
      specificEnergyConsumption, // kWh/kmol
      stageCompositions: this.stageCompositions,
      streams: this.streams
    };
  }
  
  /**
   * Initialize all streams based on active units
   */
  initializeStreams() {
    // Plant inlet stream is already created as this.streams.feed
    
    // Field compression output
    if (this.activeUnits.fieldCompression) {
      this.streams.fieldCompressionOut = this.streams.feed.clone();
    } else {
      this.streams.fieldCompressionOut = this.streams.feed;
    }
    
    // Initialize recycle streams with zero flow
    this.recycleStreams.stabilizationGas = new Stream(
      25, // default temperature
      this.feedConditions.pressure,
      0, // zero initial flow
      this.feedComposition // same composition as feed for initialization
    );
    
    // Initial value for inlet separation feed is fieldCompressionOut + stabilization recycle
    this.streams.inletSeparationFeed = this.streams.fieldCompressionOut.clone();
    
    // Other streams will be created during calculation
  }
  
  /**
   * Check if recycle streams have converged
   * @returns {boolean} True if converged
   */
  checkConvergence() {
    // Implement convergence check for recycle streams
    // For example, check if flow rates have changed significantly
    
    // For now, just return true to indicate convergence (placeholder)
    return true;
  }
  
  /**
   * Update recycle streams for next iteration
   */
  updateRecycleStreams() {
    // Update recycle stream properties based on current iteration results
    if (this.streams.stabilizationGasOut) {
      this.recycleStreams.stabilizationGas = this.streams.stabilizationGasOut.clone();
    }
    
    // Update the inlet separation feed with the recycle stream
    if (this.activeUnits.stabilisation) {
      this.streams.inletSeparationFeed = this.streams.fieldCompressionOut.mix(
        this.recycleStreams.stabilizationGas
      );
    } else {
      this.streams.inletSeparationFeed = this.streams.fieldCompressionOut;
    }
  }
  
  /**
   * Calculate CO2 emissions
   * @returns {number} CO2 emissions in tonnes/day
   */
  calculateCO2Emissions() {
    let totalCO2 = 0;
    
    // Aggregate CO2 emissions from all units
    for (const unit in this.energyConsumption) {
      totalCO2 += this.energyConsumption[unit].co2Emissions || 0;
    }
    
    // Convert from kg/h to tonnes/day
    return totalCO2 * 24 / 1000;
  }
  
  /**
   * Process Field Compression unit
   */
  processFieldCompression() {
    const config = this.unitConfigurations.fieldCompression;
    const inputStream = this.streams.feed;
    
    // Convert to Kelvin for calculations
    const feedTempK = inputStream.temperature + 273.15;
    
    // Get Z factor of input
    const zInput = inputStream.properties.Z;
    
    // -----------------------------------------------------------------
    // Bug 1 fix: compute gamma from the actual mixture composition,
    //            not hardcoded methane coefficients.
    // Bug 2 fix: derive mass-specific Cp from the same mixture Cp.
    // Bug 3 fix: apply Z1 to the isentropic enthalpy (real-gas work).
    // -----------------------------------------------------------------

    // Mole-fraction-weighted molar Cp of the mixture [J/(mol·K)]
    let Cp_mix_molar = 0;
    Object.keys(inputStream.composition).forEach(name => {
      const comp = Object.values(COMPONENTS).find(c => c.name === name);
      if (!comp) return;
      const xi = inputStream.composition[name];
      const [A, B, C, D] = comp.cp;
      Cp_mix_molar += xi * (A + B * feedTempK + C * feedTempK ** 2 + D * feedTempK ** 3);
    });
    // Fall back to methane if composition lookup fails
    if (Cp_mix_molar <= 0) {
      const [A, B, C, D] = COMPONENTS.METHANE.cp;
      Cp_mix_molar = A + B * feedTempK + C * feedTempK ** 2 + D * feedTempK ** 3;
    }

    // Real-gas gamma using inlet Z (Bug 3 partially addressed in calculateGamma)
    const gamma = calculateGamma(
      // Pass a synthetic single-value array so calculateGamma can compute Cp
      // We already have Cp_mix_molar; wrap it so the function works generically.
      // calculateGamma evaluates A + B*T + C*T²+ D*T³ — pass [Cp_mix_molar, 0, 0, 0]
      // at T=1 so the polynomial collapses to the constant.
      [Cp_mix_molar, 0, 0, 0],
      1,  // T=1 K so polynomial = A
      zInput
    );

    // Mass-specific Cp [kJ/(kg·K)]  (J/mol·K ÷ g/mol = J/g = kJ/kg)
    const cpAvg_kJ_kgK = Cp_mix_molar / inputStream.properties.MW;

    // Calculate number of stages based on configuration
    const stageCount = config.stageCount || 1;
    
    // Calculate compression ratio per stage
    const compressionRatio = Math.pow(
      config.dischargePresure / inputStream.pressure, 
      1 / stageCount
    );
    
    // Initialize variables for multi-stage calculation
    let totalPower = 0;
    let currentTemp = feedTempK;
    let currentPressure = inputStream.pressure;
    let currentZ = zInput;
    
    // Calculate power for each stage
    for (let stage = 0; stage < stageCount; stage++) {
      // Calculate outlet pressure for this stage
      const outletPressure = currentPressure * compressionRatio;
      
      // Calculate theoretical outlet temperature (isentropic)
      const isentropicTemp = calculateIsentropicTemperature(
        currentTemp, 
        currentPressure, 
        outletPressure, 
        currentZ, 
        gamma
      );
      
      // Convert molar flow to mass flow (kg/s)
      const massFlow = inputStream.flowRate * inputStream.properties.MW / 3600;
      
      // Bug 2 fix: use mixture Cp (not hardcoded 2.2 kJ/kg·K)
      // Bug 3 fix: multiply by Z1 — real-gas isentropic work = Z·R·T·f(γ,r)
      //            Using the enthalpy route: Δh_is = Z1 · cpAvg · ΔT
      const cpAvg = cpAvg_kJ_kgK;
      const isentropicEnthalpyChange = cpAvg * (isentropicTemp - currentTemp) * currentZ;
      
      // Calculate power for this stage
      const stagePower = calculateCompressionPower(
        massFlow,
        0, // Reference enthalpy
        isentropicEnthalpyChange,
        config.efficiency
      );
      
      totalPower += stagePower;
      
      // Calculate actual outlet temperature (cpAvg already corrected above)
      const actualOutletTemp = currentTemp + isentropicEnthalpyChange / config.efficiency / cpAvg;
      
      // Update for next stage or final output
      if (stage < stageCount - 1) {
        // Intercooling between stages
        const intercoolerTempK = (config.intercoolerTemp || 40) + 273.15;
        
        // Update conditions for next stage
        currentTemp = intercoolerTempK;
        currentPressure = outletPressure;
        
        // Recalculate Z at new conditions
        const zNew = pengRobinsonZ(
          currentTemp,
          currentPressure,
          inputStream.properties.Tc,
          inputStream.properties.Pc,
          inputStream.properties.omega
        );
        
        currentZ = zNew.Z;
      } else {
        // Final stage - update output stream
        currentTemp = actualOutletTemp;
        currentPressure = outletPressure;
      }
    }
    
    // Calculate driver fuel consumption or power requirement
    let fuelConsumption = 0;
    let co2Emissions = 0;
    
    if (config.driverType === 'gasTurbine') {
      // Gas turbine efficiency ~30-35%
      const driverEfficiency = 0.33;
      fuelConsumption = totalPower / driverEfficiency * 3.6; // GJ/h
      // Approximate CO2 emissions: 56 kg CO2 per GJ of natural gas
      co2Emissions = fuelConsumption * 56; // kg/h
    } else if (config.driverType === 'gasEngine') {
      // Gas engine efficiency ~40-45%
      const driverEfficiency = 0.42;
      fuelConsumption = totalPower / driverEfficiency * 3.6; // GJ/h
      co2Emissions = fuelConsumption * 56; // kg/h
    } else {
      // Electric motor (no direct emissions)
      fuelConsumption = totalPower * 3.6; // GJ/h (electrical)
      co2Emissions = totalPower * 0.5 * 3.6; // kg/h (assuming 0.5 kg CO2/kWh)
    }
    
    // Create output stream
    const outputStream = new Stream(
      currentTemp - 273.15,  // Convert back to °C
      currentPressure,
      inputStream.flowRate,
      inputStream.composition
    );
    
    // Store results
    this.streams.fieldCompressionOut = outputStream;
    this.energyConsumption.fieldCompression = {
      power: totalPower / 1000, // MW
      fuelConsumption, // GJ/h
      co2Emissions // kg/h
    };
    
    // Store composition data for display
    this.stageCompositions.fieldCompression = {
      input: { ...inputStream.composition },
      output: { ...outputStream.composition }
    };
    
    // Update the inlet separation feed (fieldCompressionOut + stabilization recycle)
    this.updateRecycleStreams();
  }
  
  /**
   * Process Inlet Separation unit
   */
  processInletSeparation() {
    const config = this.unitConfigurations.inletSeparation;
    const inputStream = this.streams.inletSeparationFeed;
    
    // Set operating conditions
    const operatingTemp = config.temperature; // °C
    const outletPressure = inputStream.pressure - config.pressureDrop; // bar
    
    // Convert input compositions to proper mole fractions if they aren't already
    // (This ensures we work with fractions internally regardless of UI display)
    const normalizedComposition = { ...inputStream.composition };
    const compositionSum = Object.values(normalizedComposition).reduce((sum, val) => sum + val, 0);
    
    if (compositionSum > 0) {
      Object.keys(normalizedComposition).forEach(component => {
        normalizedComposition[component] /= compositionSum;
      });
    }
    
    // Calculate vapor pressures for each component at operating temperature
    const vaporPressures = {};
    Object.keys(normalizedComposition).forEach(component => {
      const comp = Object.values(COMPONENTS).find(c => c.name === component);
      if (comp) {
        // Antoine equation for vapor pressure calculation: log(P) = A - B/(T+C)
        // Using simplified calculation based on critical properties
        const Tr = (operatingTemp + 273.15) / comp.Tc; // Reduced temperature
        if (Tr < 1) { // Subcritical
          // Approximate vapor pressure using reduced properties
          const omega = comp.omega; // Acentric factor
          const Pvp = comp.Pc * Math.exp(5.37 * (1 + omega) * (1 - 1/Tr));
          vaporPressures[component] = Pvp; // bar
        } else {
          // Supercritical - component is always vapor
          vaporPressures[component] = 1000; // Much higher than system pressure
        }
      } else {
        vaporPressures[component] = outletPressure; // Default if component not found
      }
    });
    
    // Calculate K-values (vapor/liquid ratio) for each component
    // K = vapor pressure / system pressure (Raoult's Law)
    const kValues = {};
    Object.keys(normalizedComposition).forEach(component => {
      kValues[component] = vaporPressures[component] / outletPressure;
    });
    
    // Initialize vapor and liquid compositions
    const vaporComposition = {};
    const liquidComposition = {};
    Object.keys(normalizedComposition).forEach(component => {
      vaporComposition[component] = 0;
      liquidComposition[component] = 0;
    });
    
    // Use Rachford-Rice flash calculation to determine vapor fraction
    // This ensures mass balance: z_i = V*y_i + (1-V)*x_i for each component i
    // where z_i is feed composition, y_i is vapor comp, x_i is liquid comp, V is vapor fraction
    
    // Function to evaluate Rachford-Rice equation: sum(z_i*(K_i-1)/(1+V*(K_i-1))) = 0
    function rachfordRice(V) {
      let sum = 0;
      Object.entries(normalizedComposition).forEach(([component, zi]) => {
        const Ki = kValues[component];
        if (zi > 0 && Ki > 0) {
          sum += zi * (Ki - 1) / (1 + V * (Ki - 1));
        }
      });
      return sum;
    }
    
    // Solve for vapor fraction using bisection method
    let Vmin = 0;
    let Vmax = 1;
    let V = 0.5; // Initial guess
    const tolerance = 1e-6;
    let iterations = 0;
    
    while (Math.abs(rachfordRice(V)) > tolerance && iterations < 100) {
      if (rachfordRice(V) * rachfordRice(Vmin) < 0) {
        Vmax = V;
      } else {
        Vmin = V;
      }
      V = (Vmin + Vmax) / 2;
      iterations++;
    }
    
    // Calculate vapor and liquid compositions
    Object.entries(normalizedComposition).forEach(([component, zi]) => {
      const Ki = kValues[component];
      
      // Liquid composition: x_i = z_i / (1 + V*(K_i-1))
      const xi = zi / (1 + V * (Ki - 1));
      
      // Vapor composition: y_i = K_i * x_i
      const yi = Ki * xi;
      
      liquidComposition[component] = xi;
      vaporComposition[component] = yi;
    });
    
    // Normalize compositions to ensure they sum to 1
    const vaporSum = Object.values(vaporComposition).reduce((sum, val) => sum + val, 0);
    const liquidSum = Object.values(liquidComposition).reduce((sum, val) => sum + val, 0);
    
    Object.keys(vaporComposition).forEach(component => {
      vaporComposition[component] /= vaporSum;
    });
    
    Object.keys(liquidComposition).forEach(component => {
      liquidComposition[component] /= liquidSum;
    });
    
    // Create output streams
    const vaporStream = new Stream(
      operatingTemp,
      outletPressure,
      inputStream.flowRate * V, // Vapor fraction of feed
      vaporComposition
    );
    
    const liquidStream = new Stream(
      operatingTemp,
      outletPressure,
      inputStream.flowRate * (1 - V), // Liquid fraction of feed
      liquidComposition
    );
    
    // Store results
    this.streams.inletSeparationVaporOut = vaporStream;
    this.streams.inletSeparationLiquidOut = liquidStream;
    
    // Store composition data (convert to percentage for display)
    this.stageCompositions.inletSeparation = {
      input: Object.entries(inputStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      vaporOutput: Object.entries(vaporComposition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      liquidOutput: Object.entries(liquidComposition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {})
    };
    
    // Calculate energy requirements...
    // (rest of the function with energy calculations)
  
    
    // Calculate heating/cooling duty if temperature change is needed
    let heatingDuty = 0;
    const tempChange = operatingTemp - inputStream.temperature;
    
    if (Math.abs(tempChange) > 1) {
      // Calculate heat duty using cp from Stream object
      const cpAvg = 2.2; // kJ/(kg·K) approximate for natural gas
      heatingDuty = inputStream.massFlow * 1000 / 3600 * cpAvg * tempChange; // kW
    }
    
    // Create a new stream with adjusted temperature and pressure
    const adjustedStream = new Stream(
      operatingTemp,
      outletPressure,
      inputStream.flowRate,
      inputStream.composition
    );
    
    // Perform vapor-liquid separation
    //const { vaporStream, liquidStream, vaporFraction } = adjustedStream.vaporLiquidSeparation();
    
    // Store results
    this.streams.inletSeparationVaporOut = vaporStream;
    this.streams.inletSeparationLiquidOut = liquidStream;
    
    this.energyConsumption.inletSeparation = {
      power: Math.abs(heatingDuty) / 1000, // MW
      heatingDuty: heatingDuty / 1000, // MW (positive for heating, negative for cooling)
      co2Emissions: 0 // Assumed to be included in utilities
    };
    
    // Store composition data
    this.stageCompositions.inletSeparation = {
      input: { ...inputStream.composition },
      vaporOutput: { ...vaporStream.composition },
      liquidOutput: { ...liquidStream.composition }
    };
    
    // Determine the next processing step for vapor
    if (this.activeUnits.inletCompression) {
      // Vapor to inlet compression
      this.streams.inletCompressionIn = vaporStream;
    } else if (this.activeUnits.sweetening) {
      // Vapor directly to sweetening
      this.streams.sweeteningIn = vaporStream;
    } else if (this.activeUnits.dehydration) {
      // Vapor directly to dehydration
      this.streams.dehydrationIn = vaporStream;
    } else if (this.activeUnits.dewpoint) {
      // Vapor directly to dewpoint
      this.streams.dewpointIn = vaporStream;
    } else {
      // Vapor directly to sales compression
      this.streams.salesCompressionIn = vaporStream;
    }
    
    // Liquid to stabilization if active
    if (this.activeUnits.stabilisation) {
      this.streams.stabilisationIn = liquidStream;
    }
  }
  
  /**
   * Process Inlet Compression unit
   */
  processInletCompression() {
    const config = this.unitConfigurations.inletCompression;
    const inputStream = this.streams.inletCompressionIn;
    
    // Very similar to field compression, but with potentially different parameters
    // For brevity, implementing a simplified version here
    
    // Calculate compression ratio
    const compressionRatio = config.dischargePresure / inputStream.pressure;
    
    // Simplified power calculation based on compression ratio and flow
    const powerFactor = 0.25; // kW/(kmol/h) per compression ratio
    const power = inputStream.flowRate * Math.log(compressionRatio) * powerFactor;
    
    // Calculate outlet temperature (simplified)
    const tempIncrease = 20 * Math.log(compressionRatio); // °C
    const outletTemp = inputStream.temperature + tempIncrease;
    
    // Create output stream
    const outputStream = new Stream(
      outletTemp,
      config.dischargePresure,
      inputStream.flowRate,
      inputStream.composition
    );
    
    // Store results
    this.streams.inletCompressionOut = outputStream;
    this.energyConsumption.inletCompression = {
      power: power / 1000, // MW
      co2Emissions: power * 0.5 * 3.6 // kg/h (assuming electric drive)
    };
    
    // Store composition data
    this.stageCompositions.inletCompression = {
      input: { ...inputStream.composition },
      output: { ...outputStream.composition }
    };
    
    // Determine next processing step
    if (this.activeUnits.sweetening) {
      this.streams.sweeteningIn = outputStream;
    } else if (this.activeUnits.dehydration) {
      this.streams.dehydrationIn = outputStream;
    } else if (this.activeUnits.dewpoint) {
      this.streams.dewpointIn = outputStream;
    } else {
      this.streams.salesCompressionIn = outputStream;
    }
  }
  
  /**
   * Process Stabilisation unit
   */
// Enhanced processStabilisation function with proper energy calculations

processStabilisation() {
    const config = this.unitConfigurations.stabilisation;
    const inputStream = this.streams.stabilisationIn;
    
    // Return if no input stream or zero flow
    if (!inputStream || inputStream.flowRate <= 0) {
      return;
    }
    
    // ---- STEP 1: Calculate stabilization parameters based on configuration ----
    const stabiliserType = config.stabiliserType || 'column';
    const operatingTemp = config.temperature || 80; // °C
    const operatingPressure = config.pressure || 5; // bar
    const refluxRatio = config.refluxRatio || 1.5; // For column type
    const trayCount = config.trayCount || 15; // For column type
    const rvpTarget = config.rvpTarget || 10; // psi
    
    // ---- STEP 2: Set up K-values based on component volatility ----
    // K-values represent vapor-liquid equilibrium ratios at stabilizer conditions
    const kValues = {
      [COMPONENTS.METHANE.name]: 12.0,
      [COMPONENTS.ETHANE.name]: 6.0,
      [COMPONENTS.PROPANE.name]: 2.5,
      [COMPONENTS.BUTANE.name]: 1.0,
      [COMPONENTS.PENTANE.name]: 0.3,
      [COMPONENTS.HEXANE.name]: 0.1,
      [COMPONENTS.NITROGEN.name]: 15.0,
      [COMPONENTS.CO2.name]: 5.0,
      [COMPONENTS.H2S.name]: 3.5,
      [COMPONENTS.WATER.name]: 0.05
    };
    
    // Adjust K-values based on RVP target (lower RVP = more stripping)
    const rvpAdjustmentFactor = 10 / rvpTarget;
    Object.keys(kValues).forEach(component => {
      kValues[component] *= rvpAdjustmentFactor;
    });
    
    // ---- STEP 3: Determine vapor fraction based on configuration ----
    let vaporFraction;
    
    if (stabiliserType === 'flash') {
      // Simple flash vessel has higher vapor fraction
      vaporFraction = 0.2 * (1 + rvpAdjustmentFactor/5);
    } else if (stabiliserType === 'column') {
      // Column produces less vapor (more efficient separation)
      vaporFraction = 0.15 * (1 + rvpAdjustmentFactor/5);
    } else {
      // Multi-stage flash
      vaporFraction = 0.18 * (1 + rvpAdjustmentFactor/5);
    }
    
    // ---- STEP 4: Calculate component distributions ----
    const vaporComposition = {};
    const liquidComposition = {};
    
    // Initialize compositions
    Object.keys(inputStream.composition).forEach(component => {
      vaporComposition[component] = 0;
      liquidComposition[component] = 0;
    });
    
    // Calculate compositions using the Rachford-Rice equation
    // zi = feed composition, Ki = K-value, V = vapor fraction
    // For each component: xi = zi / (1 + V*(Ki-1)) and yi = Ki*xi
    Object.entries(inputStream.composition).forEach(([component, zi]) => {
      if (zi > 0) {
        const Ki = kValues[component] || 1.0; // Default K = 1 if not specified
        
        // Calculate liquid mole fraction
        const xi = zi / (1 + vaporFraction * (Ki - 1));
        
        // Calculate vapor mole fraction
        const yi = Ki * xi;
        
        liquidComposition[component] = xi;
        vaporComposition[component] = yi;
      }
    });
    
    // Normalize compositions
    const sumVapor = Object.values(vaporComposition).reduce((sum, val) => sum + val, 0);
    const sumLiquid = Object.values(liquidComposition).reduce((sum, val) => sum + val, 0);
    
    Object.keys(vaporComposition).forEach(component => {
      vaporComposition[component] /= sumVapor;
    });
    
    Object.keys(liquidComposition).forEach(component => {
      liquidComposition[component] /= sumLiquid;
    });
    
    // ---- STEP 5: Calculate energy requirements ----
    let totalEnergy = 0; // kW
    
    // 1. Feed Preheating Energy
    // Calculate energy needed to heat the feed from input temperature to operating temperature
    const feedHeatCapacity = 2.5; // kJ/(kg·K) - approximate for hydrocarbon liquid
    const feedMassFlow = inputStream.massFlow * 1000; // kg/h
    const temperatureDelta = operatingTemp - inputStream.temperature; // °C
    
    const feedHeatingDuty = feedMassFlow * feedHeatCapacity * temperatureDelta / 3600; // kW
    const feedHeatingEnergy = Math.max(0, feedHeatingDuty); // Only positive if heating is needed
    
    // 2. Reboiler Duty (for column type)
    let reboilerDuty = 0;
    
    if (stabiliserType === 'column') {
      // Calculate reboiler duty based on:
      // - Bottom product flow rate
      // - Heat of vaporization
      // - Operating temperature/pressure
      // - RVP target and reflux ratio
      
      const bottomProductFlow = inputStream.flowRate * (1 - vaporFraction); // kmol/h
      const avgMolecularWeight = 70; // g/mol (approximate for stabilizer bottoms)
      const heatOfVaporization = 350; // kJ/kg (approximate)
      
      // Base reboiler duty
      let baseDuty = bottomProductFlow * avgMolecularWeight * heatOfVaporization / 3600; // kW
      
      // Adjust for reflux ratio (higher reflux = higher energy)
      baseDuty *= (1 + refluxRatio * 0.8);
      
      // Adjust for tray count (more trays = more efficient = less energy)
      const trayEfficiencyFactor = Math.max(0.6, Math.min(1.0, 15 / trayCount));
      baseDuty *= trayEfficiencyFactor;
      
      reboilerDuty = baseDuty;
    } else if (stabiliserType === 'multistage') {
      // Multi-stage flash has heating requirements between column and flash
      const bottomProductFlow = inputStream.flowRate * (1 - vaporFraction); // kmol/h
      const avgMolecularWeight = 70; // g/mol
      const heatOfVaporization = 300; // kJ/kg
      
      reboilerDuty = bottomProductFlow * avgMolecularWeight * heatOfVaporization / 3600 * 0.7; // kW
    } else {
      // Simple flash has minimal heating (just to maintain temperature)
      reboilerDuty = feedHeatingEnergy * 0.2; // kW
    }
    
    // 3. Condenser Duty (for column type)
    let condenserDuty = 0;
    
    if (stabiliserType === 'column') {
      // Condenser duty is related to the vapor flow rate and reflux ratio
      const vaporFlow = inputStream.flowRate * vaporFraction; // kmol/h
      const totalCondenserFlow = vaporFlow * (1 + refluxRatio); // Includes reflux
      const avgMolecularWeight = 40; // g/mol (approximate for stabilizer overhead)
      const heatOfCondensation = 400; // kJ/kg (approximate)
      
      condenserDuty = totalCondenserFlow * avgMolecularWeight * heatOfCondensation / 3600; // kW
    } else if (stabiliserType === 'multistage') {
      // Multi-stage flash has some cooling requirements
      const vaporFlow = inputStream.flowRate * vaporFraction; // kmol/h
      const avgMolecularWeight = 40; // g/mol
      const heatOfCondensation = 350; // kJ/kg
      
      condenserDuty = vaporFlow * avgMolecularWeight * heatOfCondensation / 3600 * 0.5; // kW
    } else {
      // Simple flash has minimal cooling
      const vaporFlow = inputStream.flowRate * vaporFraction; // kmol/h
      const avgMolecularWeight = 40; // g/mol
      const heatOfCondensation = 350; // kJ/kg
      
      condenserDuty = vaporFlow * avgMolecularWeight * heatOfCondensation / 3600 * 0.3; // kW
    }
    
    // 4. Reflux Pump Energy (for column type)
    let pumpEnergy = 0;
    
    if (stabiliserType === 'column') {
      // Calculate reflux pump power
      const refluxFlow = inputStream.flowRate * vaporFraction * refluxRatio; // kmol/h
      const refluxDensity = 500; // kg/m³ (approximate)
      const avgMolecularWeight = 40; // g/mol
      const volumetricFlow = refluxFlow * avgMolecularWeight / (refluxDensity * 3600); // m³/s
      const pressureDifferential = 3; // bar (pump head)
      const pumpEfficiency = 0.7; // 70% efficiency
      
      pumpEnergy = volumetricFlow * pressureDifferential * 1e5 / pumpEfficiency; // kW
    } else {
      // Other types have minimal pumping requirements
      pumpEnergy = 1.0; // kW (minimal)
    }
    
    // Sum up energy streams (heating is positive, cooling is negative)
    const heatingEnergy = feedHeatingEnergy + reboilerDuty; // kW
    const coolingEnergy = -condenserDuty; // kW (negative value)
    const electricalEnergy = pumpEnergy; // kW
    
    // For total energy consumption, we count heating and electrical
    // Cooling is typically provided by utilities so we track it separately
    totalEnergy = heatingEnergy + electricalEnergy;
    
    // ---- STEP 6: Create output streams ----
    const vaporStream = new Stream(
      80, // Typical overhead temperature
      operatingPressure,
      inputStream.flowRate * vaporFraction,
      vaporComposition
    );
    
    const liquidStream = new Stream(
      operatingTemp,
      operatingPressure,
      inputStream.flowRate * (1 - vaporFraction),
      liquidComposition
    );
    
    // ---- STEP 7: Store results ----
    // Store streams
    this.streams.stabilizationGasOut = vaporStream;
    this.streams.stabilizationLiquidOut = liquidStream;
    
    // Store energy consumption
    this.energyConsumption.stabilisation = {
      power: totalEnergy / 1000, // MW
      heatingDuty: heatingEnergy / 1000, // MW
      coolingDuty: coolingEnergy / 1000, // MW (negative value)
      electricalDuty: electricalEnergy / 1000, // MW
      feedHeating: feedHeatingEnergy / 1000, // MW
      reboiler: reboilerDuty / 1000, // MW
      condenser: condenserDuty / 1000, // MW
      pump: pumpEnergy / 1000, // MW
      co2Emissions: 0 // Included in utilities
    };
    
    // Store composition data
    this.stageCompositions.stabilisation = {
      input: { ...inputStream.composition },
      vaporOutput: { ...vaporComposition },
      liquidOutput: { ...liquidComposition }
    };
  }
  
  /**
   * Process remaining units (simplified implementations)
   * Add detailed implementations as needed for each process
   */
  processWasteWaterDisposal() {
    // Implementation details would go here
  }
  
/**
 * Improved Sweetening Process with flash tank and pump power calculations
 */
processSweetening() {
    const config = this.unitConfigurations.sweetening;
    const inputStream = this.streams.sweeteningIn;
    
    if (!inputStream || inputStream.flowRate <= 0) {
      return;
    }
    
    // Extract configuration parameters
    const solventType = config.solventType || 'mdea';
    const h2sInletContent = config.h2sInlet || 1000; // ppm
    const co2InletContent = config.co2Inlet || 3; // mol%
    const h2sOutletSpec = config.h2sOutlet || 4; // ppm
    const co2OutletSpec = config.co2Outlet || 2; // mol%
    const absorberPressure = config.absorberPressure || 70; // bar
    const regeneratorPressure = config.regeneratorPressure || 1.5; // bar
    const solventRate = config.solventRate || 100; // m³/h
    
    // Calculate actual H2S and CO2 content in input stream
    const actualH2sContent = inputStream.composition[COMPONENTS.H2S.name] * 1e6 || h2sInletContent; // ppm
    const actualCo2Content = inputStream.composition[COMPONENTS.CO2.name] * 100 || co2InletContent; // mol%
    
    // Calculate removal requirements
    const h2sRemovalRequired = Math.max(0, actualH2sContent - h2sOutletSpec) / 1e6; // mol fraction
    const co2RemovalRequired = Math.max(0, actualCo2Content - co2OutletSpec) / 100; // mol fraction
    
    // Calculate removed acid gas amounts
    const h2sRemoved = inputStream.flowRate * 
                      (inputStream.composition[COMPONENTS.H2S.name] || h2sInletContent / 1e6) * 
                      (h2sRemovalRequired / (inputStream.composition[COMPONENTS.H2S.name] || h2sInletContent / 1e6));
    
    const co2Removed = inputStream.flowRate * 
                       (inputStream.composition[COMPONENTS.CO2.name] || co2InletContent / 100) * 
                       (co2RemovalRequired / (inputStream.composition[COMPONENTS.CO2.name] || co2InletContent / 100));
    
    // Total acid gas flow
    const acidGasFlow = h2sRemoved + co2Removed; // kmol/h
    
    // Calculate rich amine loading and properties
    // Typical loading: 0.3-0.5 mol acid gas / mol amine
    let amineConcentration; // mol/m³
    let acidGasLoading; // mol acid gas / mol amine
    
    switch (solventType) {
      case 'mdea':
        amineConcentration = 3500; // mol/m³ (approx 40 wt% MDEA)
        acidGasLoading = 0.4; // mol acid gas / mol amine (higher for MDEA)
        break;
      case 'dea':
        amineConcentration = 4000; // mol/m³ (approx 30 wt% DEA)
        acidGasLoading = 0.35; // mol acid gas / mol amine
        break;
      case 'mea':
        amineConcentration = 5000; // mol/m³ (approx 20 wt% MEA)
        acidGasLoading = 0.3; // mol acid gas / mol amine
        break;
      case 'sulfinol':
        amineConcentration = 3000; // mol/m³
        acidGasLoading = 0.5; // mol acid gas / mol amine (higher for physical solvents)
        break;
      default:
        amineConcentration = 3500; // mol/m³
        acidGasLoading = 0.4; // mol acid gas / mol amine
    }
    
    // Calculate required solvent circulation based on acid gas removal
    const theoreticalSolventRate = (h2sRemoved + co2Removed) / (amineConcentration * acidGasLoading) * 1000; // m³/h
    
    // Use the maximum of user-specified and calculated rates
    const actualSolventRate = Math.max(solventRate, theoreticalSolventRate * 1.2); // m³/h (20% safety factor)
    
    // Rich amine loading (actual)
    const actualLoading = (h2sRemoved + co2Removed) / (actualSolventRate * amineConcentration / 1000); // mol/mol
    
    // Selectivity for H2S vs CO2 (varies by solvent)
    let h2sSelectivity;
    
    switch (solventType) {
      case 'mdea':
        h2sSelectivity = 5; // MDEA is highly selective for H2S
        break;
      case 'sulfinol':
        h2sSelectivity = 3; // Moderate selectivity
        break;
      case 'dea':
        h2sSelectivity = 1.5; // Lower selectivity
        break;
      case 'mea':
        h2sSelectivity = 1; // No selectivity
        break;
      default:
        h2sSelectivity = 2; // Default moderate selectivity
    }
    
    // Flash tank calculation
    // Rich amine from absorber undergoes pressure reduction from absorber to flash tank
    const flashTankPressure = 5; // bar (typical)
    
    // VLE calculation for acid gas flash
    // Simplified: estimate fraction of acid gas that flashes based on pressure drop
    // Higher pressure drop = more gas flashed
    const pressureRatio = absorberPressure / flashTankPressure;
    
    // Flash gas estimation (simplified)
    // Approximately 5-15% of acid gas flashes depending on pressure drop and loading
    const flashFraction = 0.05 + 0.05 * Math.log(pressureRatio) + 0.05 * actualLoading;
    
    // Flash gas flow and composition
    const h2sFlashed = h2sRemoved * flashFraction / h2sSelectivity; // Less H2S flashes due to stronger bonding
    const co2Flashed = co2Removed * flashFraction; // CO2 flashes more readily
    
    const flashGasFlow = h2sFlashed + co2Flashed; // kmol/h
    
    // Flash gas composition
    const flashGasComposition = {};
    
    if (flashGasFlow > 0) {
      flashGasComposition[COMPONENTS.H2S.name] = h2sFlashed / flashGasFlow;
      flashGasComposition[COMPONENTS.CO2.name] = co2Flashed / flashGasFlow;
    }
    
    // Create flash gas stream
    const flashGasStream = new Stream(
      40, // °C (typical flash tank temperature)
      flashTankPressure,
      flashGasFlow,
      flashGasComposition
    );
    
    // Acid gas from regenerator
    const h2sToRegenerator = h2sRemoved - h2sFlashed; // kmol/h
    const co2ToRegenerator = co2Removed - co2Flashed; // kmol/h
    const acidGasFromRegenerator = h2sToRegenerator + co2ToRegenerator; // kmol/h
    
    // Acid gas composition from regenerator
    const acidGasComposition = {};
    
    if (acidGasFromRegenerator > 0) {
      acidGasComposition[COMPONENTS.H2S.name] = h2sToRegenerator / acidGasFromRegenerator;
      acidGasComposition[COMPONENTS.CO2.name] = co2ToRegenerator / acidGasFromRegenerator;
    }
    
    // Create acid gas stream from regenerator
    const acidGasStream = new Stream(
      60, // °C (typical regenerator overhead temperature)
      regeneratorPressure,
      acidGasFromRegenerator,
      acidGasComposition
    );
    
    // Sweet gas composition (removing acid gases from input)
    const sweetGasComposition = { ...inputStream.composition };
    
    // Adjust H2S and CO2 content to meet specifications
    if (sweetGasComposition[COMPONENTS.H2S.name]) {
      sweetGasComposition[COMPONENTS.H2S.name] = h2sOutletSpec / 1e6; // Convert ppm to mol fraction
    }
    
    if (sweetGasComposition[COMPONENTS.CO2.name]) {
      sweetGasComposition[COMPONENTS.CO2.name] = co2OutletSpec / 100; // Convert mol% to mol fraction
    }
    
    // Normalize sweet gas composition
    const sweetGasSum = Object.values(sweetGasComposition).reduce((sum, val) => sum + val, 0);
    
    Object.keys(sweetGasComposition).forEach(key => {
      sweetGasComposition[key] /= sweetGasSum;
    });
    
    // Create sweet gas output stream
    const sweetGasStream = new Stream(
      inputStream.temperature,
      absorberPressure - 0.5, // Small pressure drop across contactor
      inputStream.flowRate, // Flow rate remains approximately the same
      sweetGasComposition
    );
    
    // Calculate energy requirements
    
    // 1. Reboiler duty for solvent regeneration
    // Depends on: solvent circulation rate, solvent heat capacity, temperature rise, heat of reaction
    
    // Heat required per unit of acid gas removed (varies by solvent and loading)
    let regenerationHeat; // GJ/tonne acid gas
    
    switch (solventType) {
      case 'mdea':
        regenerationHeat = 2.5; // GJ/tonne acid gas (lower for MDEA)
        break;
      case 'sulfinol':
        regenerationHeat = 2.8; // GJ/tonne acid gas
        break;
      case 'dea':
        regenerationHeat = 3.2; // GJ/tonne acid gas
        break;
      case 'mea':
        regenerationHeat = 4.0; // GJ/tonne acid gas (higher for MEA)
        break;
      default:
        regenerationHeat = 3.0; // GJ/tonne acid gas
    }
    
    // Calculate mass of acid gas to regenerator
    const h2sMW = 34.08; // g/mol
    const co2MW = 44.01; // g/mol
    
    const acidGasMass = (h2sToRegenerator * h2sMW + co2ToRegenerator * co2MW) / 1000; // tonnes/h
    
    // Reboiler duty
    const reboilerDuty = acidGasMass * regenerationHeat / 3.6; // MW
    
    // 2. Pump power for solvent circulation
    // Pumping from regenerator pressure back to absorber pressure
    
    // Amine density
    const amineDensity = 1000; // kg/m³ (approximate)
    
    // Pressure increase
    const pumpDeltaP = absorberPressure - regeneratorPressure; // bar
    
    // Pump efficiency
    const pumpEfficiency = 0.7; // 70% efficiency
    
    // Pump power calculation
    const pumpPower = actualSolventRate * amineDensity * 9.81 * pumpDeltaP * 100000 / 
                     (3600 * 1000000000 * pumpEfficiency); // MW
    
    // Total energy consumption
    const totalEnergy = reboilerDuty + pumpPower; // MW
    
    // Store results
    this.streams.sweetGasOut = sweetGasStream;
    this.streams.acidGasOut = acidGasStream;
    this.streams.flashGasOut = flashGasStream;
    
    // Store energy consumption
    this.energyConsumption.sweetening = {
      power: totalEnergy, // MW
      reboilerDuty: reboilerDuty, // MW
      pumpPower: pumpPower, // MW
      solventRate: actualSolventRate, // m³/h
      loading: actualLoading, // mol/mol
      co2Emissions: 0 // Included in utilities
    };
    
    // Store composition data (convert to percentage for display)
    this.stageCompositions.sweetening = {
      input: Object.entries(inputStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      sweetGasOutput: Object.entries(sweetGasComposition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      acidGasOutput: Object.entries(acidGasComposition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      flashGasOutput: Object.entries(flashGasComposition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {})
    };
    
    // Route streams to appropriate destinations
    
    // Route sweet gas to next unit
    if (this.activeUnits.dehydration) {
      this.streams.dehydrationIn = sweetGasStream;
    } else if (this.activeUnits.dewpoint) {
      this.streams.dewpointIn = sweetGasStream;
    } else {
      this.streams.salesCompressionIn = sweetGasStream;
    }
    
    // Route acid gas to sulfur recovery if activated
    if (config.acidGasDisposal === 'sru') {
      this.streams.sulfurRecoveryIn = acidGasStream;
    }
    
    // Route flash gas to inlet compression
    if (this.activeUnits.inletCompression) {
      // If inlet compression stream already exists, mix with flash gas
      if (this.streams.inletCompressionIn) {
        this.streams.inletCompressionIn = this.streams.inletCompressionIn.mix(flashGasStream);
      } else {
        this.streams.inletCompressionIn = flashGasStream;
      }
    }
  

  }

/**
 * Improved Sulfur Recovery Unit with air blower, multiple converters, and proper mass balance
 */
processSulfurRecovery() {
    // Only process if we have acid gas from sweetening
    if (!this.streams.acidGasOut || this.streams.acidGasOut.flowRate <= 0) {
      // No acid gas, no sulfur recovery
      return;
    }
    
    const inputStream = this.streams.acidGasOut;
    
    // Verify we have H2S in the stream
    if (!inputStream.composition[COMPONENTS.H2S.name] || 
        inputStream.composition[COMPONENTS.H2S.name] <= 0.01) {
      // Negligible H2S, not worth recovering
      return;
    }
    
    // Calculate H2S and CO2 flow rates
    const h2sFlow = inputStream.flowRate * inputStream.composition[COMPONENTS.H2S.name]; // kmol/h
    const co2Flow = inputStream.flowRate * inputStream.composition[COMPONENTS.CO2.name] || 0; // kmol/h
    
    // Claus process chemistry:
    // Step 1: Thermal stage (1/3 of H2S is burned)
    // H2S + 1.5 O2 → SO2 + H2O (combustion - exothermic)
    
    // Step 2: Catalytic stages (2/3 of H2S reacts with SO2)
    // 2 H2S + SO2 → 3 S + 2 H2O (Claus reaction - exothermic)
    
    // Overall: 3 H2S + 1.5 O2 → 3 S + 3 H2O
    
    // Amount of H2S for each reaction path
    const h2sForCombustion = h2sFlow / 3; // 1/3 for combustion
    const h2sForClaus = h2sFlow * 2 / 3; // 2/3 for Claus reaction
    
    // Calculate oxygen required for combustion
    // H2S + 1.5 O2 → SO2 + H2O
    const o2Required = h2sForCombustion * 1.5; // kmol/h
    
    // Air required (21% oxygen)
    const airRequired = o2Required / 0.21; // kmol/h
    const airMW = 28.97; // g/mol
    const airMassFlow = airRequired * airMW / 1000; // tonnes/h
    
    // Calculate the amount of sulfur produced
    // Overall: 3 H2S → 3 S
    const sulfurProduction = h2sFlow; // kmol/h
    const sulfurMW = 32.065; // g/mol
    const sulfurMassFlow = sulfurProduction * sulfurMW / 1000; // tonnes/h
    
    // Calculate energy from reactions
    
    // Step 1: H2S combustion
    // H2S + 1.5 O2 → SO2 + H2O
    // Heat of combustion: -518 kJ/mol H2S
    const combustionHeat = h2sForCombustion * 518; // kJ/h
    
    // Step 2: Claus reaction
    // 2 H2S + SO2 → 3 S + 2 H2O
    // Heat of reaction: -108 kJ/mol SO2 (or -54 kJ/mol H2S in the Claus reaction)
    const clausHeat = h2sForClaus * 54; // kJ/h
    
    // Total exothermic heat release
    const totalExothermicHeat = (combustionHeat + clausHeat) / 3600; // MW
    
    // Energy recovered as steam (typically 60-80% of heat can be recovered)
    const heatRecoveryEfficiency = 0.7;
    const heatRecovered = totalExothermicHeat * heatRecoveryEfficiency; // MW
    
    // Air blower power calculation
    // Typical pressure rise: 0.5 bar
    // Typical air blower efficiency: 70%
    const blowerPressureRise = 0.5; // bar
    const blowerEfficiency = 0.7;
    
    // Convert air flow to volume at STP
    const airVolumeFlow = airRequired * 22.4 / 1000; // m³/h at STP
    
    // Air blower power (adiabatic compression)
    // W = (flow * dP) / efficiency
    const blowerPower = airVolumeFlow * blowerPressureRise * 100000 / (3600 * blowerEfficiency); // kW
    
    // Multiple catalytic converters
    const converterCount = 3; // Typical Claus plant has 2-3 converters
    
    // Sulfur recovery per stage
    // Thermal stage: ~60-70%
    // 1st converter: Additional 20-25%
    // 2nd converter: Additional 5-10%
    // 3rd converter: Additional 1-2%
    const stageRecovery = [0.65, 0.22, 0.08, 0.02]; // Fraction of total H2S converted in each stage
    
    // Total theoretical recovery
    const totalRecovery = stageRecovery.slice(0, converterCount + 1).reduce((sum, val) => sum + val, 0);
    
    // Actual recovery (accounting for real-world inefficiencies)
    const actualRecovery = Math.min(0.98, totalRecovery * 0.97); // Max 98% realistic recovery
    
    // Unconverted H2S
    const unconvertedH2S = h2sFlow * (1 - actualRecovery); // kmol/h
    
    // Reheating energy for each converter
    // Each catalytic converter needs feed to be ~200-240°C
    const reheatDutyPerConverter = 0.05; // MW per converter (simplified estimate)
    const totalReheatDuty = reheatDutyPerConverter * converterCount; // MW
    
    // Calculate tail gas flow and composition
    // Tail gas includes unconverted H2S, CO2, N2 from air, and trace other components
    
    // Nitrogen from air (79% of air)
    const nitrogenFlow = airRequired * 0.79; // kmol/h
    
    // Total tail gas flow
    const tailGasFlow = unconvertedH2S + co2Flow + nitrogenFlow; // kmol/h
    
    // Tail gas composition
    const tailGasComposition = {};
    
    // Add components if flow is significant
    if (unconvertedH2S > 0.001) {
      tailGasComposition[COMPONENTS.H2S.name] = unconvertedH2S / tailGasFlow;
    }
    
    if (co2Flow > 0.001) {
      tailGasComposition[COMPONENTS.CO2.name] = co2Flow / tailGasFlow;
    }
    
    if (nitrogenFlow > 0.001) {
      tailGasComposition[COMPONENTS.NITROGEN.name] = nitrogenFlow / tailGasFlow;
    }
    
    // Tail gas incinerator
    // Burns remaining H2S to SO2 and requires additional fuel
    
    // Energy required for incineration
    // Need to heat tail gas to ~800°C for complete combustion
    const tailGasSpecificHeat = 1.0; // kJ/(kg·K) (approximate)
    const tailGasMW = 30; // g/mol (approximate average)
    
    // Temperature rise from process temperature to incinerator temperature
    const temperatureRise = 300 - 250; // °C
    
    // Heat required for tail gas
    const tailGasMassFlow = tailGasFlow * tailGasMW / 1000; // tonnes/h
    const incineratorHeatingDuty = tailGasMassFlow * 1 * tailGasSpecificHeat * temperatureRise / 3600; // MW
    
    // Additional fuel required for incineration
    // Natural gas has heating value of ~50 MJ/kg
    const fuelHeatingValue = 50; // MJ/kg
    const incineratorEfficiency = 0.95;
    
    // Additional fuel for incinerator
    const incineratorFuel = incineratorHeatingDuty / (fuelHeatingValue * incineratorEfficiency / 3.6); // tonnes/h
    
    // CO2 emissions from incinerator
    // Natural gas produces ~2.75 kg CO2 per kg burned
    // Plus CO2 from H2S combustion
    const co2FromFuel = incineratorFuel * 2.75 * 1000; // kg/h
    const co2FromH2S = unconvertedH2S * 44 / 1000 * 1000; // kg/h (1 mol H2S → 1 mol CO2)
    
    const totalCO2Emissions = co2FromFuel + co2FromH2S;
    
    // Total energy balance for SRU
    const energyConsumption = blowerPower / 1000 + totalReheatDuty + incineratorHeatingDuty; // MW
    const netEnergy = energyConsumption - heatRecovered; // MW (positive means energy consumption)
    
    // Create tail gas stream
    const tailGasStream = new Stream(
      300, // temperature at incinerator outlet
      1.0, // atmospheric pressure
      tailGasFlow,
      tailGasComposition
    );
    
    // Create sulfur product stream (liquid sulfur)
    const sulfurStream = {
      temperature: 140, // °C (liquid sulfur)
      massFlow: sulfurMassFlow // tonnes/h
    };
    
    // Store results
    this.streams.sulfurRecoveryTailGas = tailGasStream;
    this.streams.sulfurProduct = sulfurStream;
    
    this.energyConsumption.sulfurRecovery = {
      power: netEnergy, // MW (negative means net production)
      heatRecovered: heatRecovered, // MW
      blowerPower: blowerPower / 1000, // MW
      reheatDuty: totalReheatDuty, // MW
      incineratorDuty: incineratorHeatingDuty, // MW
      totalExothermicHeat: totalExothermicHeat, // MW
      sulfurProduction: sulfurMassFlow, // tonnes/h
      sulfurRecovery: actualRecovery * 100, // percentage
      co2Emissions: totalCO2Emissions // kg/h
    };
    
    // Store composition data
    this.stageCompositions.sulfurRecovery = {
      input: Object.entries(inputStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      tailGas: Object.entries(tailGasComposition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {})
    };
  }

/**
 * Process Dehydration unit
 */
processDehydration() {
    const config = this.unitConfigurations.dehydration;
    const inputStream = this.streams.dehydrationIn;
    
    if (!inputStream || inputStream.flowRate <= 0) {
      return;
    }
    
    // Extract configuration parameters
    const dehydrationMethod = config.dehydrationMethod || 'glycol';
    const inletWaterContent = config.inletWaterContent || 120; // lb/MMscf
    const outletWaterSpec = config.outletWaterSpec || 4; // lb/MMscf
    
    // Convert water content from lb/MMscf to mole fraction for calculations
    // 1 lbmol water = 18.02 lb
    // 1 MMscf gas at STP contains approximately 1000 lbmol (varies with composition)
    // Water content in mol fraction ≈ (lb water / 18.02) / (1000 lbmol gas)
    const MMscfFactor = 379.5; // scf/lbmol at 60°F and 14.7 psia
    const waterMW = 18.02; // lb/lbmol
    
    // Convert inlet and outlet water content to mole fractions
    const inletWaterMolFraction = (inletWaterContent / waterMW) / (1000000 / MMscfFactor);
    const outletWaterMolFraction = (outletWaterSpec / waterMW) / (1000000 / MMscfFactor);
    
    // Get water content from input stream composition, or use estimated inlet water content
    let currentWaterMolFraction = 0;
    if (inputStream.composition[COMPONENTS.WATER.name]) {
      currentWaterMolFraction = inputStream.composition[COMPONENTS.WATER.name];
    } else {
      currentWaterMolFraction = inletWaterMolFraction;
    }
    
    // Calculate water removal rate in kg/h
    const waterRemovalMolFraction = Math.max(0, currentWaterMolFraction - outletWaterMolFraction);
    const waterRemovalRate = inputStream.flowRate * waterRemovalMolFraction * COMPONENTS.WATER.MW / 1000; // tonnes/h
    
    // Create output composition - copy input but reduce water content
    const outputComposition = { ...inputStream.composition };
    if (outputComposition[COMPONENTS.WATER.name]) {
      outputComposition[COMPONENTS.WATER.name] = outletWaterMolFraction;
    }
    
    // Normalize composition
    const compositionSum = Object.values(outputComposition).reduce((sum, val) => sum + val, 0);
    if (compositionSum > 0) {
      Object.keys(outputComposition).forEach(component => {
        outputComposition[component] /= compositionSum;
      });
    }
    
    // Calculate energy requirements based on dehydration method and water removal
    let energyConsumption = 0; // kW
    
    if (dehydrationMethod === 'glycol') {
      // TEG (Triethylene Glycol) System
      // Energy primarily for reboiler to regenerate glycol
      
      // Typical glycol circulation rate: 2-4 gallons TEG per lb of water removed
      const glycolRate = config.glycolRate || 25; // L/kg H₂O
      
      // Convert to proper units (m³/h)
      const waterRemovalRateKg = waterRemovalRate * 1000; // kg/h
      const glycolCirculationRate = waterRemovalRateKg * glycolRate / 1000; // m³/h
      
      // Reboiler duty:
      // - Heat required to raise TEG from absorber to reboiler temperature
      // - Heat required to vaporize water from rich TEG
      // - Heat losses
      
      const absTemp = inputStream.temperature; // °C
      const regenTemp = config.reboilerTemp || 204; // °C (typically 375-400°F)
      
      // Specific heat of TEG: ~2.2 kJ/(kg·°C)
      // TEG density: ~1120 kg/m³
      const tegSpecificHeat = 2.2; // kJ/(kg·°C)
      const tegDensity = 1120; // kg/m³
      
      // Heat required to raise TEG temperature
      const tegMassFlow = glycolCirculationRate * tegDensity; // kg/h
      const tegHeating = tegMassFlow * tegSpecificHeat * (regenTemp - absTemp) / 3600; // kW
      
      // Heat required to vaporize water (latent heat of vaporization)
      const waterEvapHeat = 2260; // kJ/kg at atmospheric pressure
      const waterEvaporation = waterRemovalRateKg * waterEvapHeat / 3600; // kW
      
      // Heat losses (typically 10-15% of total heat)
      const heatLoss = 0.15 * (tegHeating + waterEvaporation);
      
      // Total reboiler duty
      const reboilerDuty = tegHeating + waterEvaporation + heatLoss; // kW
      
      // Stripping gas if used (additional energy for compression)
      let strippingGasCompression = 0;
      if (config.regenerationMethod === 'stripping') {
        // Typical stripping gas: 3-5 SCF per gallon of TEG
        const strippingGasRate = 4 * glycolCirculationRate * 3.78541; // SCF/h (4 SCF/gal × gal/h)
        strippingGasCompression = strippingGasRate * 0.000075; // kW (approximate)
      }
      
      // Pumping power for glycol circulation
      // Pump head ~200 psi, efficiency ~70%
      const pumpPressure = 14; // bar (~200 psi)
      const pumpEfficiency = 0.7;
      const pumpPower = glycolCirculationRate * pumpPressure * 100000 / (3600 * pumpEfficiency); // kW
      
      // Total energy consumption
      energyConsumption = reboilerDuty + strippingGasCompression + pumpPower;
    } 
    else if (dehydrationMethod === 'molecularSieve') {
      // Molecular Sieve System
      // Energy for heating regeneration gas and bed cooling
      
      // Typical design: 3-4 beds (one in regeneration, rest adsorbing)
      const bedCount = config.bedCount || 3;
      const cycleDuration = 8; // hours for complete cycle
      const regenTime = cycleDuration / bedCount; // hours of regeneration per bed
      
      // Water adsorption capacity: ~10-13 wt% of bed
      // Typically use 4A molecular sieve
      const waterCapacity = 0.12; // kg water/kg sieve
      
      // Calculate required bed size based on water removal and cycle time
      const waterRemovalPeriod = waterRemovalRate * 1000 * cycleDuration; // kg water per cycle
      const sieveRequired = waterRemovalPeriod / waterCapacity; // kg sieve
      
      // Regeneration gas requirements:
      // - Typically use 2-5% of feed gas
      // - Heat to 260-315°C (500-600°F)
      const regenGasRate = 0.05 * inputStream.flowRate; // kmol/h
      const regenGasMW = inputStream.properties.MW; // g/mol
      const regenGasMassFlow = regenGasRate * regenGasMW / 1000; // tonnes/h
      
      // Regeneration temperature
      const regenTemp = config.regenerationTemp || 290; // °C
      
      // Heat required to raise regeneration gas temperature
      // Specific heat of gas ~2.5 kJ/(kg·°C)
      const gasSpecificHeat = 2.5; // kJ/(kg·°C)
      const regenGasHeating = regenGasMassFlow * 1000 * gasSpecificHeat * (regenTemp - inputStream.temperature) / 3600; // kW
      
      // Heat required for bed regeneration
      // - Heat for moisture desorption
      // - Heat to warm the bed
      
      // Bed specific heat ~1.0 kJ/(kg·°C)
      const bedSpecificHeat = 1.0; // kJ/(kg·°C)
      
      // Calculate bed mass per active bed (total divided by operating beds)
      const activeBeds = bedCount - 1; // one bed in regeneration
      const bedMass = sieveRequired / activeBeds; // kg
      
      // Heat to warm bed material
      const bedHeating = bedMass * bedSpecificHeat * (regenTemp - inputStream.temperature) / (regenTime * 3600); // kW
      
      // Heat for moisture desorption (includes heat of adsorption)
      // Heat of adsorption for water on zeolite ~4200 kJ/kg
      const waterDesorptionHeat = 4200; // kJ/kg
      const waterDesorption = (waterRemovalRate * 1000 / activeBeds) * waterDesorptionHeat / 3600; // kW
      
      // Total regeneration heating
      const regenHeating = regenGasHeating + bedHeating + waterDesorption;
      
      // Cooling requirements for regenerated bed before switching
      // Usually not counted in energy consumption, just cooling water use
      
      // Pressure drop compensation (blowers or additional compression)
      // Typical pressure drop ~0.3-0.5 bar
      const pressureDrop = config.pressureDrop || 0.5; // bar
      const gasVolumeFlow = inputStream.flowRate * 22.4 / 1000; // m³/h at STP
      const pressureDropCompensation = gasVolumeFlow * pressureDrop * 100000 / (3600 * 0.7); // kW
      
      // Total energy consumption
      energyConsumption = regenHeating + pressureDropCompensation;
    } 
    else {
      // Other methods (silica gel, membranes)
      // Simplified calculation
      energyConsumption = waterRemovalRate * 1000 * 1000; // kW (very approximate)
    }
    
    // Create output stream
    const outputStream = new Stream(
      inputStream.temperature,
      inputStream.pressure - (config.pressureDrop || 0.5),
      inputStream.flowRate,
      outputComposition
    );
    
    // Store results
    this.streams.dehydrationOut = outputStream;
    
    this.energyConsumption.dehydration = {
      power: energyConsumption / 1000, // MW
      waterRemovalRate: waterRemovalRate, // tonnes/h
      co2Emissions: 0 // Included in utilities
    };
    
    // Store composition data (convert to percentage for display)
    this.stageCompositions.dehydration = {
      input: Object.entries(inputStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      output: Object.entries(outputComposition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {})
    };
        
        // Store composition data
        this.stageCompositions.dehydration = {
          input: { ...inputStream.composition },
          output: { ...outputStream.composition }
        };
        
        // Determine next processing step
        if (this.activeUnits.dewpoint) {
          this.streams.dewpointIn = outputStream;
        } else {
          this.streams.salesCompressionIn = outputStream;
        }
  }
    

  
  
/**
 * Process Dewpoint Control / NGL Recovery unit
 */
processDewpoint() {
    const config = this.unitConfigurations.dewpoint;
    const inputStream = this.streams.dewpointIn;
    
    if (!inputStream || inputStream.flowRate <= 0) {
      return;
    }
    
    // Get configuration parameters
    const processType = config.processType || 'refrigeration';
    const refrigerationTemp = config.refrigerationTemp || -35; // °C
    const jtInletPressure = config.jtInletPressure || 80; // bar
    const jtOutletPressure = config.jtOutletPressure || 30; // bar
    const expanderEfficiency = config.expanderEfficiency || 0.8;
    const compressorEfficiency = config.compressorEfficiency || 0.75;
    
    // Initialize energy consumption variables
    let refrigerationDuty = 0; // kW
    let refrigerationPower = 0; // kW
    let compressionPower = 0; // kW
    let expanderPower = 0; // kW
    let netPower = 0; // kW
    
    // Function to calculate heat capacity of the gas
    const calculateGasHeatCapacity = (stream) => {
      // Approximate heat capacity of natural gas components (kJ/kg·K)
      const componentCp = {
        [COMPONENTS.METHANE.name]: 2.2,
        [COMPONENTS.ETHANE.name]: 1.8,
        [COMPONENTS.PROPANE.name]: 1.7,
        [COMPONENTS.BUTANE.name]: 1.7,
        [COMPONENTS.PENTANE.name]: 1.7,
        [COMPONENTS.HEXANE.name]: 1.6,
        [COMPONENTS.NITROGEN.name]: 1.04,
        [COMPONENTS.CO2.name]: 0.85,
        [COMPONENTS.H2S.name]: 1.0,
        [COMPONENTS.WATER.name]: 1.9
      };
      
      // Calculate weighted average based on composition
      let cpMix = 0;
      Object.entries(stream.composition).forEach(([component, moleFraction]) => {
        const cp = componentCp[component] || 2.0; // Default if not found
        cpMix += cp * moleFraction;
      });
      
      return cpMix;
    };
    
    // Function to calculate enthalpy change for cooling/heating
    const calculateEnthalpyChange = (stream, T1, T2) => {
      const cp = calculateGasHeatCapacity(stream); // kJ/kg·K
      const massFlow = stream.massFlow * 1000 / 3600; // kg/s
      return massFlow * cp * (T2 - T1); // kW
    };
    
    // Function to estimate final temperature after JT expansion
    const calculateJTExpansionTemp = (stream, P1, P2) => {
      // Joule-Thomson coefficient for natural gas (approximately 0.5 K/bar)
      const jtCoefficient = 0.5; // K/bar
      const temperatureDrop = jtCoefficient * (P1 - P2);
      return stream.temperature - temperatureDrop;
    };
    
    // Function to calculate adiabatic expansion temperature
    const calculateAdiabaticExpansionTemp = (stream, P1, P2, efficiency) => {
      // For natural gas, gamma (Cp/Cv) is approximately 1.3
      const gamma = 1.3;
      
      // Ideal gas adiabatic expansion: T2 = T1 * (P2/P1)^((gamma-1)/gamma)
      const temperatureRatio = Math.pow(P2 / P1, (gamma - 1) / gamma);
      const idealFinalTemp = (stream.temperature + 273.15) * temperatureRatio - 273.15; // °C
      
      // Actual temperature accounting for efficiency (less cooling with higher efficiency)
      // Efficiency affects the isentropic path, not directly the temperature
      const actualDeltaT = (idealFinalTemp - stream.temperature) / efficiency;
      return stream.temperature + actualDeltaT;
    };
    
    // Function to calculate compressor power
    const calculateCompressorPower = (stream, P1, P2, T1, efficiency) => {
      // For natural gas, gamma (Cp/Cv) is approximately 1.3
      const gamma = 1.3;
      const Z = stream.properties.Z || 0.9; // Compressibility factor
      
      // Power = mass flow × Z × R × T1 / MW × (gamma/(gamma-1)) × ((P2/P1)^((gamma-1)/gamma) - 1) / efficiency
      const massFlow = stream.massFlow * 1000 / 3600; // kg/s
      const MW = stream.properties.MW / 1000; // kg/mol
      const R = 8.314; // J/(mol·K)
      const T1K = T1 + 273.15; // K
      
      const term = (gamma / (gamma - 1)) * (Math.pow(P2 / P1, (gamma - 1) / gamma) - 1);
      const power = massFlow * Z * R * T1K / MW * term / efficiency;
      
      return power / 1000; // MW
    };
    
    // Results variables
    let residueGasStream = null;
    let nglStream = null;
    
    // Process based on selected type
    if (processType === 'refrigeration') {
      // ---- REFRIGERATION PROCESS ----
      // 1. Product/feed heat exchange - cool feed using cold residue gas
      // 2. Refrigeration cooling to target temperature
      // 3. Phase separation
      // 4. Reheating of residue gas through product/feed exchanger
      
      // Initial setup
      const targetTemp = refrigerationTemp; // °C
      const feedTemp = inputStream.temperature; // °C
      const feedPressure = inputStream.pressure; // bar
      
      // First, create a copy of input stream for intermediate calculations
      let cooledStream = inputStream.clone();
      
      // Assume initially that we can cool the feed to an intermediate temperature
      // through product/feed heat exchange (will be refined through iteration)
      let intermediateTemp = (feedTemp + targetTemp) / 2;
      
      // Calculate initial cooling duty (feed to intermediate temperature)
      const initialCoolingDuty = calculateEnthalpyChange(
        inputStream, 
        feedTemp, 
        intermediateTemp
      ); // kW
      
      // Refrigeration cooling (intermediate to target temperature)
      const refrigerationCoolingDuty = calculateEnthalpyChange(
        cooledStream,
        intermediateTemp,
        targetTemp
      ); // kW
      
      // Update cooled stream temperature
      cooledStream.temperature = targetTemp;
      
      // Perform VLE separation at the cold temperature
      const vleResult = cooledStream.vaporLiquidSeparation();
      const coldVaporStream = vleResult.vaporStream;
      const coldLiquidStream = vleResult.liquidStream;
      
      // This is our NGL stream
      nglStream = coldLiquidStream;
      
      // Calculate reheating of residue gas through product/feed exchanger
      // (heat recovered from cooling the feed)
      const reheatDuty = calculateEnthalpyChange(
        coldVaporStream,
        targetTemp,
        intermediateTemp
      ); // kW
      
      // In a real heat exchanger, reheatDuty should be approximately equal to initialCoolingDuty
      // Adjust intermediate temperature through iteration to balance the heat exchange
      // (Simplified here - in practice this would need several iterations)
      
      // Final residue gas stream after reheating
      residueGasStream = coldVaporStream.clone();
      residueGasStream.temperature = intermediateTemp;
      
      // Calculate net refrigeration duty (what the refrigeration system must provide)
      refrigerationDuty = refrigerationCoolingDuty; // kW
      
      // Convert refrigeration duty to power using coefficient of performance (COP)
      // COP depends on temperature levels: COP ≈ T_evap / (T_cond - T_evap)
      // Convert temperatures to absolute (K)
      const T_evap = targetTemp + 273.15; // K
      const T_cond = 35 + 273.15; // K (typical condensing temperature)
      const idealCOP = T_evap / (T_cond - T_evap);
      
      // Real systems achieve 40-60% of ideal COP
      const realCOP = idealCOP * 0.5;
      
      // Calculate power required
      refrigerationPower = Math.abs(refrigerationDuty) / realCOP / 1000; // MW
      
      // Total power consumption
      netPower = refrigerationPower;
    }
    else if (processType === 'jtExpansion') {
      // ---- JOULE-THOMSON EXPANSION PROCESS ----
      // 1. Product/feed heat exchange - cool feed using cold residue gas
      // 2. Refrigeration pre-cooling if specified
      // 3. JT expansion (isenthalpic) 
      // 4. Phase separation
      // 5. Reheating and recompression of residue gas
      
      // Initial setup
      const precoolingTemp = config.precoolingTemp || 15; // °C
      const feedTemp = inputStream.temperature; // °C
      
      // Create a copy of input stream for intermediate calculations
      let cooledStream = inputStream.clone();
      
      // Pre-cooling to specified temperature
      refrigerationDuty = 0;
      if (precoolingTemp < feedTemp) {
        refrigerationDuty = calculateEnthalpyChange(
          inputStream,
          feedTemp,
          precoolingTemp
        ); // kW
        
        // Convert refrigeration duty to power
        const T_evap = precoolingTemp + 273.15; // K
        const T_cond = 35 + 273.15; // K
        const idealCOP = T_evap / (T_cond - T_evap);
        const realCOP = idealCOP * 0.5;
        refrigerationPower = Math.abs(refrigerationDuty) / realCOP / 1000; // MW
        
        // Update stream temperature
        cooledStream.temperature = precoolingTemp;
      }
      
      // JT expansion (isenthalpic)
      const jtFinalTemp = calculateJTExpansionTemp(
        cooledStream,
        jtInletPressure,
        jtOutletPressure
      );
      
      // Create expanded stream
      let expandedStream = cooledStream.clone();
      expandedStream.temperature = jtFinalTemp;
      expandedStream.pressure = jtOutletPressure;
      
      // Perform VLE separation after expansion
      const vleResult = expandedStream.vaporLiquidSeparation();
      const expandedVaporStream = vleResult.vaporStream;
      const expandedLiquidStream = vleResult.liquidStream;
      
      // This is our NGL stream
      nglStream = expandedLiquidStream;
      
      // Residue gas after reheating (simplified - would be calculated from heat exchange)
      residueGasStream = expandedVaporStream.clone();
      residueGasStream.temperature = Math.min(feedTemp, expandedVaporStream.temperature + 15);
      
      // Calculate compression power to bring residue gas back to sales pressure
      const salesPressure = 80; // bar (typical)
      compressionPower = calculateCompressorPower(
        residueGasStream,
        jtOutletPressure,
        salesPressure,
        residueGasStream.temperature,
        compressorEfficiency
      ); // MW
      
      // Total power consumption
      netPower = refrigerationPower + compressionPower;
    }
    else if (processType === 'turboExpander') {
      // ---- TURBOEXPANDER PROCESS ----
      // 1. Product/feed heat exchange - cool feed using cold residue gas
      // 2. Refrigeration pre-cooling if needed
      // 3. Adiabatic expansion through turboexpander
      // 4. Phase separation
      // 5. Reheating and recompression of residue gas
      // 6. Work recovery from expander to help drive compressor
      
      // Initial setup
      const feedTemp = inputStream.temperature; // °C
      
      // Create a copy of input stream for intermediate calculations
      let cooledStream = inputStream.clone();
      
      // Optional pre-cooling stage (simplified)
      let intermediateTemp = 0; // °C (typical pre-cooling target)
      if (feedTemp > intermediateTemp) {
        refrigerationDuty = calculateEnthalpyChange(
          inputStream,
          feedTemp,
          intermediateTemp
        ); // kW
        
        // Convert refrigeration duty to power
        const T_evap = intermediateTemp + 273.15; // K
        const T_cond = 35 + 273.15; // K
        const idealCOP = T_evap / (T_cond - T_evap);
        const realCOP = idealCOP * 0.5;
        refrigerationPower = Math.abs(refrigerationDuty) / realCOP / 1000; // MW
        
        // Update stream temperature
        cooledStream.temperature = intermediateTemp;
      }
      
      // Turboexpander adiabatic expansion
      const expanderOutletPressure = jtOutletPressure; // Use same as JT for comparison
      const expanderOutletTemp = calculateAdiabaticExpansionTemp(
        cooledStream,
        cooledStream.pressure,
        expanderOutletPressure,
        expanderEfficiency
      );
      
      // Create expanded stream
      let expandedStream = cooledStream.clone();
      expandedStream.temperature = expanderOutletTemp;
      expandedStream.pressure = expanderOutletPressure;
      
      // Perform VLE separation after expansion
      const vleResult = expandedStream.vaporLiquidSeparation();
      const expandedVaporStream = vleResult.vaporStream;
      const expandedLiquidStream = vleResult.liquidStream;
      
      // This is our NGL stream
      nglStream = expandedLiquidStream;
      
      // Residue gas after reheating (simplified - would be calculated from heat exchange)
      residueGasStream = expandedVaporStream.clone();
      residueGasStream.temperature = Math.min(feedTemp, expandedVaporStream.temperature + 20);
      
      // Calculate power generated by turboexpander
      // Power = mass flow × cp × (T1 - T2) × efficiency
      const massFlow = cooledStream.massFlow * 1000 / 3600; // kg/s
      const cp = calculateGasHeatCapacity(cooledStream); // kJ/kg·K
      expanderPower = massFlow * cp * (cooledStream.temperature - expanderOutletTemp) * expanderEfficiency / 1000; // MW
      
      // Calculate compression power to bring residue gas back to sales pressure
      const salesPressure = 80; // bar (typical)
      compressionPower = calculateCompressorPower(
        residueGasStream,
        expanderOutletPressure,
        salesPressure,
        residueGasStream.temperature,
        compressorEfficiency
      ); // MW
      
      // Net power consumption (compression power minus expander power recovery)
      netPower = refrigerationPower + compressionPower - expanderPower;
    }
    else if (processType === 'deepcut') {
      // Similar to turboexpander but with lower temperatures
      // and additional refrigeration to achieve deeper ethane recovery
      
      // Use similar calculation as turboexpander but with modified parameters
      // Set up for deeper ethane recovery
      const feedTemp = inputStream.temperature; // °C
      const deepCutTemp = -85; // °C (lower than standard processes)
      
      // Create a copy of input stream for intermediate calculations
      let cooledStream = inputStream.clone();
      
      // Pre-cooling with refrigeration to achieve lower temperatures
      refrigerationDuty = calculateEnthalpyChange(
        inputStream,
        feedTemp,
        deepCutTemp
      ); // kW
      
      // Convert refrigeration duty to power (lower COP at these temperatures)
      const T_evap = deepCutTemp + 273.15; // K
      const T_cond = 35 + 273.15; // K
      const idealCOP = T_evap / (T_cond - T_evap);
      const realCOP = idealCOP * 0.4; // Lower efficiency at very low temperatures
      refrigerationPower = Math.abs(refrigerationDuty) / realCOP / 1000; // MW
      
      // Update stream temperature
      cooledStream.temperature = deepCutTemp;
      
      // Perform VLE separation at the deep-cut temperature
      const vleResult = cooledStream.vaporLiquidSeparation();
      const coldVaporStream = vleResult.vaporStream;
      const coldLiquidStream = vleResult.liquidStream;
      
      // This is our NGL stream
      nglStream = coldLiquidStream;
      
      // Residue gas after reheating (simplified)
      residueGasStream = coldVaporStream.clone();
      residueGasStream.temperature = Math.min(feedTemp, coldVaporStream.temperature + 30);
      
      // Net power consumption (primarily refrigeration for deep-cut)
      netPower = refrigerationPower;
    }
    
    // Ensure we have valid output streams
    if (!residueGasStream || !nglStream) {
      console.error("Dewpoint calculation failed to produce valid output streams");
      return;
    }
    
    // Store results
    this.streams.dewpointResidueGasOut = residueGasStream;
    this.streams.dewpointNglOut = nglStream;
    
    this.energyConsumption.dewpoint = {
      power: netPower, // MW
      refrigerationDuty: refrigerationDuty / 1000, // MW
      refrigerationPower: refrigerationPower, // MW
      compressionPower: compressionPower, // MW
      expanderPower: expanderPower, // MW
      co2Emissions: netPower * 500 // kg/h (approximate)
    };
    
    // Store composition data (convert to percentage for display)
    this.stageCompositions.dewpoint = {
      input: Object.entries(inputStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      residueGasOutput: Object.entries(residueGasStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {}),
      nglOutput: Object.entries(nglStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {})
    };
    
    // Store results
    this.streams.dewpointResidueGasOut = residueGasStream;
    this.streams.dewpointNglOut = nglStream;
    

    
    // Store composition data
    this.stageCompositions.dewpoint = {
      input: { ...inputStream.composition },
      residueGasOutput: { ...residueGasStream.composition },
      nglOutput: { ...nglStream.composition }
    };
    
    // Determine next processing steps
    if (this.activeUnits.salesCompression) {
      this.streams.salesCompressionIn = residueGasStream;
    }
    
    if (this.activeUnits.fractionation) {
      this.streams.fractionationIn = nglStream;
    }
  }
  
  /**
 * Process Fractionation unit with improved mass balance and energy calculations
 */
processFractionation() {
    const config = this.unitConfigurations.fractionation;
    const inputStream = this.streams.fractionationIn;
    
    if (!inputStream || inputStream.flowRate <= 0) {
      return;
    }
    
    // Get configuration parameters
    const configuration = config.configuration || 'conventional';
    const deethanizer = config.deethanizer || false;
    const depropanizer = config.depropanizer || true;
    const debutanizer = config.debutanizer || true;
    
    // Tower pressures (kPa -> bar)
    const deethanizer_pressure = 22.0; // bar (2200 kPa)
    const depropanizer_pressure = 15.0; // bar (1500 kPa)
    const debutanizer_pressure = 5.0;   // bar (500 kPa)
    
    // Initialize product streams and energy consumption
    const products = {};
    const energy = {
      reboilerDuty: {},
      condenserDuty: {},
      pumpPower: {}
    };
    
    // Total energy consumption
    let totalEnergy = 0; // MW
    
    // Copy input composition for mass balance tracking
    let remainingComposition = { ...inputStream.composition };
    let remainingFlow = inputStream.flowRate;
    
    // Function to calculate reboiler duty
    const calculateReboilerDuty = (feed, vaporFraction, bottomTemp, heatOfVaporization) => {
      // Simplified reboiler duty calculation
      // Duty = vapor flow × heat of vaporization + feed heating
      const reboilerVaporFlow = feed.flowRate * vaporFraction;
      const vaporization = reboilerVaporFlow * heatOfVaporization; // kW
      
      // Heating feed to reboiler temperature (simplified)
      const feedHeating = calculateHeatingDuty(feed, feed.temperature, bottomTemp);
      
      return vaporization + feedHeating;
    };
    
    // Function to calculate condenser duty
    const calculateCondenserDuty = (overhead, condenserTemp, heatOfCondensation) => {
      // Condenser duty = overhead flow × heat of condensation
      return overhead.flowRate * heatOfCondensation;
    };
    
    // Function to calculate reflux pump power
    const calculatePumpPower = (refluxFlow, pressureDrop, efficiency) => {
      // Reflux pump power = flow × pressure drop / efficiency
      const density = 500; // kg/m³ (approximate for light hydrocarbons)
      const volumetricFlow = refluxFlow * 0.03 / 3600; // m³/s (rough conversion)
      return volumetricFlow * pressureDrop * 100000 / efficiency; // W
    };
    
    // Function to calculate heating duty
    const calculateHeatingDuty = (stream, initialTemp, finalTemp) => {
      // Specific heat of liquid hydrocarbons ~ 2.5 kJ/(kg·K)
      const cp = 2.5; // kJ/(kg·K)
      const massFlow = stream.massFlow * 1000 / 3600; // kg/s
      return massFlow * cp * (finalTemp - initialTemp); // kW
    };
    
    // Function to calculate cooling duty
    const calculateCoolingDuty = (stream, initialTemp, finalTemp) => {
      // Similar to heating but for cooling
      return -calculateHeatingDuty(stream, initialTemp, finalTemp); // kW (negative for cooling)
    };
    
    // Function to create product stream
    const createProductStream = (name, composition, flowRate, temperature, pressure) => {
      // Normalize composition
      const total = Object.values(composition).reduce((sum, val) => sum + val, 0);
      const normalizedComposition = {};
      
      Object.entries(composition).forEach(([component, value]) => {
        normalizedComposition[component] = value / total;
      });
      
      // Create and return new stream
      return new Stream(
        temperature,
        pressure,
        flowRate,
        normalizedComposition
      );
    };
    
    // ---- DEETHANIZER (if enabled) ----
    if (deethanizer) {
      // Deethanizer separates methane and ethane from heavier components
      // Top product: C1, C2
      // Bottom product: C3+
      
      // Operating conditions
      const towerPressure = deethanizer_pressure;
      const towerBottomTemp = 90; // °C
      const condenserTemp = -20; // °C (refrigerated condenser needed for ethane)
      const refluxRatio = config.deetha_reflux || 5;
      
      // Recovery factors (fraction of component going to its preferred product)
      const recoveryFactor = {
        [COMPONENTS.METHANE.name]: 0.999, // 99.9% of methane goes overhead
        [COMPONENTS.ETHANE.name]: 0.98,   // 98% of ethane goes overhead
        [COMPONENTS.PROPANE.name]: 0.02,  // 2% of propane goes overhead
        // Heavier components stay in bottoms
      };
      
      // Initialize overhead and bottoms compositions
      const overheadComp = {};
      const bottomsComp = {};
      let overheadFlow = 0;
      let bottomsFlow = 0;
      
      // Calculate component distributions
      Object.entries(remainingComposition).forEach(([component, moleFraction]) => {
        // Get recovery factor (default to 0 for overhead if not specified)
        const recovery = recoveryFactor[component] || 0;
        
        // Calculate flow of this component to overhead and bottoms
        const componentFlow = remainingFlow * moleFraction;
        const toOverhead = componentFlow * recovery;
        const toBottoms = componentFlow - toOverhead;
        
        // Add to overhead
        overheadComp[component] = toOverhead;
        overheadFlow += toOverhead;
        
        // Add to bottoms
        bottomsComp[component] = toBottoms;
        bottomsFlow += toBottoms;
      });
      
      // Create overhead (ethane) product stream
      const ethaneProduct = createProductStream(
        'ethane',
        overheadComp,
        overheadFlow,
        condenserTemp,
        towerPressure
      );
      
      products['ethane'] = ethaneProduct;
      
      // Calculate energy requirements
      
      // 1. Reboiler duty
      // Heat of vaporization for propane-rich mixture ~ 350 kJ/kg
      const deethaReboilerDuty = calculateReboilerDuty(
        inputStream,
        0.4, // Vapor fraction in reboiler (approximate)
        towerBottomTemp,
        350 // kJ/kg
      ); // kW
      
      // 2. Condenser duty
      // Heat of condensation for ethane ~ 490 kJ/kg
      const deethaCondenserDuty = calculateCondenserDuty(
        ethaneProduct,
        condenserTemp,
        490 // kJ/kg
      ); // kW
      
      // 3. Reflux pump power
      const refluxFlow = overheadFlow * refluxRatio;
      const deethaRefluxPower = calculatePumpPower(
        refluxFlow,
        2, // bar pressure drop
        0.7 // pump efficiency
      ); // W
      
      // Store energy values
      energy.reboilerDuty['deethanizer'] = deethaReboilerDuty / 1000; // MW
      energy.condenserDuty['deethanizer'] = deethaCondenserDuty / 1000; // MW
      energy.pumpPower['deethanizer'] = deethaRefluxPower / 1000000; // MW
      
      // Add to total energy (reboiler + pump, condenser handled by utilities)
      totalEnergy += energy.reboilerDuty['deethanizer'] + energy.pumpPower['deethanizer'];
      
      // Update remaining stream for next tower
      remainingComposition = { ...bottomsComp };
      Object.keys(remainingComposition).forEach(component => {
        remainingComposition[component] /= bottomsFlow;
      });
      remainingFlow = bottomsFlow;
    }
    
    // ---- DEPROPANIZER (if enabled) ----
    if (depropanizer) {
      // Depropanizer separates propane from heavier components
      // Top product: C3 (with trace C2 if deethanizer disabled)
      // Bottom product: C4+
      
      // Operating conditions
      const towerPressure = depropanizer_pressure;
      const towerBottomTemp = 120; // °C
      const condenserTemp = 40; // °C
      const refluxRatio = config.depro_reflux || 3;
      
      // Recovery factors adjusted based on whether deethanizer is present
      const recoveryFactor = {
        [COMPONENTS.METHANE.name]: 0.999, // Any remaining methane goes overhead
        [COMPONENTS.ETHANE.name]: 0.99,   // Any remaining ethane goes overhead
        [COMPONENTS.PROPANE.name]: 0.98,  // 98% of propane goes overhead
        [COMPONENTS.BUTANE.name]: 0.02,   // 2% of butane goes overhead
        // Heavier components stay in bottoms
      };
      
      // Initialize overhead and bottoms compositions
      const overheadComp = {};
      const bottomsComp = {};
      let overheadFlow = 0;
      let bottomsFlow = 0;
      
      // Calculate component distributions
      Object.entries(remainingComposition).forEach(([component, moleFraction]) => {
        // Get recovery factor (default to 0 for overhead if not specified)
        const recovery = recoveryFactor[component] || 0;
        
        // Calculate flow of this component to overhead and bottoms
        const componentFlow = remainingFlow * moleFraction;
        const toOverhead = componentFlow * recovery;
        const toBottoms = componentFlow - toOverhead;
        
        // Add to overhead
        overheadComp[component] = toOverhead;
        overheadFlow += toOverhead;
        
        // Add to bottoms
        bottomsComp[component] = toBottoms;
        bottomsFlow += toBottoms;
      });
      
      // Create overhead (propane) product stream
      const propaneProduct = createProductStream(
        'propane',
        overheadComp,
        overheadFlow,
        condenserTemp,
        towerPressure
      );
      
      products['propane'] = propaneProduct;
      
      // Calculate energy requirements
      
      // 1. Reboiler duty
      // Heat of vaporization for butane-rich mixture ~ 380 kJ/kg
      const deproReboilerDuty = calculateReboilerDuty(
        {
          flowRate: remainingFlow,
          temperature: inputStream.temperature,
          massFlow: remainingFlow * 44 / 1000 // Approximate using propane MW
        },
        0.35, // Vapor fraction in reboiler (approximate)
        towerBottomTemp,
        380 // kJ/kg
      ); // kW
      
      // 2. Condenser duty
      // Heat of condensation for propane ~ 330 kJ/kg
      const deproCondenserDuty = calculateCondenserDuty(
        propaneProduct,
        condenserTemp,
        330 // kJ/kg
      ); // kW
      
      // 3. Reflux pump power
      const refluxFlow = overheadFlow * refluxRatio;
      const deproRefluxPower = calculatePumpPower(
        refluxFlow,
        1.5, // bar pressure drop
        0.7 // pump efficiency
      ); // W
      
      // Store energy values
      energy.reboilerDuty['depropanizer'] = deproReboilerDuty / 1000; // MW
      energy.condenserDuty['depropanizer'] = deproCondenserDuty / 1000; // MW
      energy.pumpPower['depropanizer'] = deproRefluxPower / 1000000; // MW
      
      // Add to total energy
      totalEnergy += energy.reboilerDuty['depropanizer'] + energy.pumpPower['depropanizer'];
      
      // Update remaining stream for next tower
      remainingComposition = { ...bottomsComp };
      Object.keys(remainingComposition).forEach(component => {
        remainingComposition[component] /= bottomsFlow;
      });
      remainingFlow = bottomsFlow;
    }
    
    // ---- DEBUTANIZER (if enabled) ----
    if (debutanizer) {
      // Debutanizer separates butane from heavier components
      // Top product: C4
      // Bottom product: C5+ (natural gasoline)
      
      // Operating conditions
      const towerPressure = debutanizer_pressure;
      const towerBottomTemp = 140; // °C
      const condenserTemp = 45; // °C
      const refluxRatio = config.debuta_reflux || 3;
      
      // Recovery factors
      const recoveryFactor = {
        [COMPONENTS.PROPANE.name]: 0.999, // Any remaining propane goes overhead
        [COMPONENTS.BUTANE.name]: 0.98,   // 98% of butane goes overhead
        [COMPONENTS.PENTANE.name]: 0.02,  // 2% of pentane goes overhead
        // Heavier components stay in bottoms
      };
      
      // Initialize overhead and bottoms compositions
      const overheadComp = {};
      const bottomsComp = {};
      let overheadFlow = 0;
      let bottomsFlow = 0;
      
      // Calculate component distributions
      Object.entries(remainingComposition).forEach(([component, moleFraction]) => {
        // Get recovery factor (default to 0 for overhead if not specified)
        const recovery = recoveryFactor[component] || 0;
        
        // Calculate flow of this component to overhead and bottoms
        const componentFlow = remainingFlow * moleFraction;
        const toOverhead = componentFlow * recovery;
        const toBottoms = componentFlow - toOverhead;
        
        // Add to overhead
        overheadComp[component] = toOverhead;
        overheadFlow += toOverhead;
        
        // Add to bottoms
        bottomsComp[component] = toBottoms;
        bottomsFlow += toBottoms;
      });
      
      // Create overhead (butane) product stream
      const butaneProduct = createProductStream(
        'butane',
        overheadComp,
        overheadFlow,
        condenserTemp,
        towerPressure
      );
      
      products['butane'] = butaneProduct;
      
      // Create bottoms (natural gasoline) product stream
      const gasolineProduct = createProductStream(
        'naturalGasoline',
        bottomsComp,
        bottomsFlow,
        towerBottomTemp,
        towerPressure
      );
      
      products['naturalGasoline'] = gasolineProduct;
      
      // Calculate energy requirements
      
      // 1. Reboiler duty
      // Heat of vaporization for C5+ mixture ~ 360 kJ/kg
      const debutaReboilerDuty = calculateReboilerDuty(
        {
          flowRate: remainingFlow,
          temperature: inputStream.temperature,
          massFlow: remainingFlow * 58 / 1000 // Approximate using butane MW
        },
        0.3, // Vapor fraction in reboiler (approximate)
        towerBottomTemp,
        360 // kJ/kg
      ); // kW
      
      // 2. Condenser duty
      // Heat of condensation for butane ~ 390 kJ/kg
      const debutaCondenserDuty = calculateCondenserDuty(
        butaneProduct,
        condenserTemp,
        390 // kJ/kg
      ); // kW
      
      // 3. Reflux pump power
      const refluxFlow = overheadFlow * refluxRatio;
      const debutaRefluxPower = calculatePumpPower(
        refluxFlow,
        1.0, // bar pressure drop
        0.7 // pump efficiency
      ); // W
      
      // Store energy values
      energy.reboilerDuty['debutanizer'] = debutaReboilerDuty / 1000; // MW
      energy.condenserDuty['debutanizer'] = debutaCondenserDuty / 1000; // MW
      energy.pumpPower['debutanizer'] = debutaRefluxPower / 1000000; // MW
      
      // Add to total energy
      totalEnergy += energy.reboilerDuty['debutanizer'] + energy.pumpPower['debutanizer'];
    }
    else {
      // If no debutanizer, the remaining stream is the natural gasoline product
      const gasolineProduct = createProductStream(
        'naturalGasoline',
        remainingComposition,
        remainingFlow,
        60, // °C
        5 // bar
      );
      
      products['naturalGasoline'] = gasolineProduct;
    }
    
    // Apply heat integration effects if enabled
    if (config.heatIntegration === 'high') {
      totalEnergy *= 0.7; // 30% savings with high integration
    } else if (config.heatIntegration === 'medium') {
      totalEnergy *= 0.85; // 15% savings with medium integration
    }
    
    // Store results
    this.streams.fractionationProducts = products;
    
    this.energyConsumption.fractionation = {
      power: totalEnergy, // MW
      reboilerDuty: energy.reboilerDuty, // MW by tower
      condenserDuty: energy.condenserDuty, // MW by tower
      pumpPower: energy.pumpPower, // MW by tower
      co2Emissions: 0 // Included in utilities
    };
    
    // Store composition data for display
    const stageCompositions = {
      input: Object.entries(inputStream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {})
    };
    
    // Add product compositions
    Object.entries(products).forEach(([product, stream]) => {
      stageCompositions[product] = Object.entries(stream.composition).reduce((obj, [key, val]) => {
        obj[key] = val * 100; // Convert to percentage
        return obj;
      }, {});
    });
    
    this.stageCompositions.fractionation = stageCompositions;
  
    // Store results
    //this.streams.fractionationProducts = productStreams;
    
    /*this.energyConsumption.fractionation = {
      power: energy, // MW
      co2Emissions: 0 // Included in utilities
    };*/
    
    // Store composition data
    this.stageCompositions.fractionation = {
      input: { ...inputStream.composition }
    };
    
    // Add products to stage compositions
    Object.entries(products).forEach(([product, data]) => {
      if (data.flowRate > 0) {
        this.stageCompositions.fractionation[product] = { ...data.composition };
      }
    });
  }
  
  processSalesCompression() {
    const config = this.unitConfigurations.salesCompression;
    const inputStream = this.streams.salesCompressionIn;
    // Get sales pressure from configuration or feed conditions
    const salesPressure = config.dischargePressure || 
    this.feedConditions.salesPressure || 
    80; // bar (default if not specified)
    // Calculate compression ratio
    const compressionRatio = salesPressure / inputStream.pressure;
    
    // Calculate power requirement
    // Power scales with flow rate, compression ratio, and gas properties
    const powerFactor = 0.2; // kW/(kmol/h) per ln(compression ratio)
    const power = inputStream.flowRate * Math.log(compressionRatio) * powerFactor;
    
    // Adjust for efficiency
    const actualPower = power / config.efficiency;
    
    // Calculate outlet temperature (simplified)
    const tempIncrease = 15 * Math.log(compressionRatio); // °C
    const outletTemp = inputStream.temperature + tempIncrease;
    
    // After-cooling
    const finalTemp = config.aftercoolerType === 'none' ? 
      outletTemp : config.coolingTemp || 40;
    
    // Calculate cooling duty if needed
    let coolingDuty = 0;
    if (finalTemp < outletTemp) {
      const cpAvg = 2.2; // kJ/(kg·K) approximate for natural gas
      coolingDuty = inputStream.massFlow * 1000 / 3600 * cpAvg * (outletTemp - finalTemp); // kW
    }
    
    // Create output stream
    const outputStream = new Stream(
      finalTemp,
      config.dischargePressure,
      inputStream.flowRate,
      inputStream.composition
    );
    
    // Calculate emissions based on driver type
    let co2Emissions = 0;
    
    if (config.driverType === 'gasTurbine') {
      // Gas turbine efficiency ~30-35%
      const driverEfficiency = 0.33;
      co2Emissions = actualPower / driverEfficiency * 0.2 * 3600; // kg/h
    } else if (config.driverType === 'gasEngine') {
      // Gas engine efficiency ~40-45%
      const driverEfficiency = 0.42;
      co2Emissions = actualPower / driverEfficiency * 0.18 * 3600; // kg/h
    } else {
      // Electric motor (grid emissions)
      co2Emissions = actualPower * 0.5 * 3600; // kg/h (0.5 kg/kWh)
    }
    
    // Store results
    this.streams.salesGasOut = outputStream;
    
    this.energyConsumption.salesCompression = {
      power: actualPower / 1000, // MW
      coolingDuty: coolingDuty / 1000, // MW
      co2Emissions // kg/h
    };
    
    // Store composition data
    this.stageCompositions.salesCompression = {
      input: { ...inputStream.composition },
      output: { ...outputStream.composition }
    };
  }
  
/**
 * Process Utilities unit with proper energy accounting
 */
processUtilities() {
    const utilityType = this.activeUnits.utilities;
    const config = this.unitConfigurations.utilities;
    
    if (utilityType === 'none') {
      return;
    }
    
    // Calculate total process heat and power required by all units
    let totalHeatDemand = 0;  // MW (thermal)
    let totalPowerDemand = 0; // MW (electrical)
    
    // Collect heat and power demands from all process units
    Object.entries(this.energyConsumption).forEach(([unit, consumption]) => {
      // Skip utilities to avoid circular reference
      if (unit === 'utilities') return;
      
      // Extract heating duty if available
      if (consumption.heatingDuty) {
        totalHeatDemand += consumption.heatingDuty;
      } else if (consumption.reboilerDuty) {
        // Add up all reboiler duties if specified by tower
        if (typeof consumption.reboilerDuty === 'object') {
          Object.values(consumption.reboilerDuty).forEach(duty => {
            totalHeatDemand += duty;
          });
        } else {
          totalHeatDemand += consumption.reboilerDuty;
        }
      }
      
      // Extract electrical power requirements
      // For compression units, all power is electrical
      if (unit.includes('Compression') && consumption.power) {
        totalPowerDemand += consumption.power;
      } 
      // For other units, check for specific electrical requirements
      else if (consumption.electricalDuty) {
        totalPowerDemand += consumption.electricalDuty;
      } else if (consumption.pumpPower) {
        // Add up all pump powers if specified by equipment
        if (typeof consumption.pumpPower === 'object') {
          Object.values(consumption.pumpPower).forEach(power => {
            totalPowerDemand += power;
          });
        } else {
          totalPowerDemand += consumption.pumpPower;
        }
      }
    });
    
    // Calculate utility energy consumption based on type
    let fuelConsumption = 0;      // MW
    let powerGeneration = 0;      // MW
    let boilerStackLosses = 0;    // MW
    let pumpPower = 0;            // MW
    let heatLosses = 0;           // MW
    let co2Emissions = 0;         // kg/h
    let auxiliaryPower = 0;       // MW
    
    if (utilityType === 'rankine') {
      // Rankine cycle parameters
      const boilerEfficiency = config.boilerEfficiency || 0.85;
      const turbineEfficiency = config.turbineEfficiency || 0.85;
      const pumpEfficiency = config.pumpEfficiency || 0.75;
      const cycleEfficiency = 0.25; // Typical for simple Rankine cycle
      
      // Calculate necessary boiler output to meet heat and power demands
      // Power demand / cycle efficiency = total thermal input needed for power
      const thermalInputForPower = totalPowerDemand / cycleEfficiency;
      
      // Total thermal demand is heat demand + thermal input for power
      const totalThermalDemand = totalHeatDemand + thermalInputForPower;
      
      // Fuel required accounting for boiler efficiency
      fuelConsumption = totalThermalDemand / boilerEfficiency;
      
      // Boiler stack losses
      boilerStackLosses = fuelConsumption * (1 - boilerEfficiency);
      
      // Feedwater pump power for the cycle
      // Approximate as 1-2% of thermal input
      pumpPower = totalThermalDemand * 0.015;
      
      // Heat losses in piping, etc. (approximately 5% of thermal demand)
      heatLosses = totalThermalDemand * 0.05;
      
      // Auxiliary power for fans, controls, etc. (approximately 1% of thermal input)
      auxiliaryPower = fuelConsumption * 0.01;
      
      // Total power generation (meets demand plus auxiliary equipment)
      powerGeneration = totalPowerDemand + pumpPower + auxiliaryPower;
      
      // CO2 emissions from fuel combustion
      co2Emissions = fuelConsumption * 200; // kg/h per MW (natural gas)
    } 
    else if (utilityType === 'heatMedium') {
      // Heat medium system
      const heaterEfficiency = config.heaterEfficiency || 0.85;
      
      // Calculate fuel required for heat demand
      fuelConsumption = totalHeatDemand / heaterEfficiency;
      
      // Heater stack losses
      boilerStackLosses = fuelConsumption * (1 - heaterEfficiency);
      
      // Circulation pump power
      const circulationRate = config.circulationRate || 150; // m³/h
      const pumpHead = 5; // bar (typical)
      const pumpEff = 0.7; // 70% efficiency
      pumpPower = circulationRate * pumpHead * 100000 / (3600 * 1000000 * pumpEff); // MW
      
      // Heat losses in piping (based on user-specified loss rate)
      const heatLossRate = config.heatLoss || 0.05; // fraction
      heatLosses = totalHeatDemand * heatLossRate;
      
      // Auxiliary power for controls, fans, etc.
      auxiliaryPower = 0.05; // MW (fixed estimate)
      
      // Need to import power for pumps and auxiliaries
      powerGeneration = 0;
      
      // CO2 emissions from fuel combustion
      co2Emissions = fuelConsumption * 200; // kg/h per MW (natural gas)
    }
    
    // Calculate net utility energy consumption
    // This is the additional energy needed beyond what's already counted in process units
    const netUtilityConsumption = boilerStackLosses + heatLosses + pumpPower + auxiliaryPower;
    
    // Store results
    this.energyConsumption.utilities = {
      power: netUtilityConsumption, // MW (additional consumption beyond process units)
      fuelConsumption: fuelConsumption, // MW (total fuel input)
      powerGeneration: powerGeneration, // MW (electrical output for Rankine)
      heatSupplied: totalHeatDemand, // MW (heat supplied to process)
      electricalSupplied: totalPowerDemand, // MW (power supplied to process)
      boilerLosses: boilerStackLosses, // MW
      heatLosses: heatLosses, // MW
      pumpPower: pumpPower, // MW
      auxiliaryPower: auxiliaryPower, // MW
      type: utilityType,
      co2Emissions: co2Emissions // kg/h
    };
  
    

  }
}

export default PlantModel;